import type { HeaderContentDefinition, ToolPanelDefinition } from '../plugins/shell/types';
import { DEFAULT_ANIMATION_CONFIG, DEFAULT_GRID_ICONS } from './defaults';
import {
  announceDataLoaded,
  createAriaState,
  updateAriaCounts,
  updateAriaLabels,
  type AriaState,
} from './internal/aria';
import { normalizeColumns, type ColumnShorthand } from './internal/column-shorthand';
import { autoSizeColumns, interleaveHiddenColumns, updateTemplate } from './internal/columns';
import { ConfigManager } from './internal/config-manager';
import { INVALID_ATTRIBUTE_JSON, warnDiagnostic } from './internal/diagnostics';
import { buildBareGridDOMIntoElement } from './internal/dom-builder';
import { updateEmptyOverlay, type EmptyOverlayState } from './internal/empty';
import { setupCellEventDelegation, setupRootEventDelegation } from './internal/event-delegation';
import { resolveFeatures } from './internal/feature-hook';
import { FocusManager, setupFocusTracking } from './internal/focus-manager';
import { renderHeader } from './internal/header';
import { cancelIdle, scheduleIdle } from './internal/idle-scheduler';
import { ensureCellVisible } from './internal/keyboard';
import {
  createLoadingOverlay,
  hideLoadingOverlay,
  setCellLoadingState,
  setRowLoadingState,
  showLoadingOverlay,
} from './internal/loading';
import { RenderPhase, RenderScheduler } from './internal/render-scheduler';
import { createResizeController } from './internal/resize';
import { animateRow, animateRowById, animateRows } from './internal/row-animation';
import { resolveRowIdOrThrow, RowManager, tryResolveRowId } from './internal/row-manager';
import { releasePooledRow } from './internal/row-teardown';
import { invalidateCellCache, renderVisibleRows } from './internal/rows';
import { applySort, reapplyCoreSort, toggleSort } from './internal/sorting';
import { addPluginStyles, injectStyles } from './internal/style-injector';
import { getActiveGridTag, GRID_TAG_NAME, setActiveGridTag } from './internal/tag-registry';
import {
  cancelMomentum,
  createTouchScrollState,
  setupTouchScrollListeners,
  setupWheelScrollListeners,
  type TouchScrollState,
} from './internal/touch-scroll';
import {
  validatePluginConfigRules,
  validatePluginIncompatibilities,
  validatePluginProperties,
} from './internal/validate-config';
import { readCellField } from './internal/value-accessor';
import { computeRowsTranslateY } from './internal/virtualization';
import { VirtualizationManager } from './internal/virtualization-manager';
import type { AfterCellRenderContext, AfterRowRenderContext, CellMouseEvent, ScrollEvent } from './plugin';
import type { BaseGridPlugin, CellClickEvent, HeaderClickEvent, RowClickEvent } from './plugin/base-plugin';
import { PluginManager } from './plugin/plugin-manager';
import { gridStyles as styles } from './styles';
import type {
  AnimationConfig,
  ColumnConfig,
  ColumnConfigMap,
  ColumnInferenceMode,
  ColumnInternal,
  DataChangeDetail,
  DataGridEventMap,
  FitMode,
  FrameworkAdapter,
  GridColumnState,
  GridConfig,
  IconValue,
  InternalGrid,
  PluginNameMap,
  ResizeController,
  RowAnimationType,
  RowElementInternal,
  RowTransaction,
  ScrollToRowOptions,
  TbwScrollDetail,
  TransactionResult,
  UpdateSource,
  VirtualState,
} from './types';

/**
 * High-performance data grid web component.
 *
 * ## Instantiation
 *
 * **Do not call the constructor directly.** Web components must be created via
 * the DOM API. Use one of these approaches:
 *
 * ```typescript
 * // Recommended: Use the createGrid() factory for TypeScript type safety
 * import { createGrid, SelectionPlugin } from '@toolbox-web/grid/all';
 *
 * const grid = createGrid<Employee>({
 *   columns: [
 *     { field: 'name', header: 'Name' },
 *     { field: 'email', header: 'Email' }
 *   ],
 *   plugins: [new SelectionPlugin()]
 * });
 * grid.rows = employees;
 * document.body.appendChild(grid);
 *
 * // Alternative: Query existing element from DOM
 * import { queryGrid } from '@toolbox-web/grid';
 * const grid = queryGrid<Employee>('#my-grid');
 *
 * // Alternative: Use document.createElement (loses type inference)
 * const grid = document.createElement('tbw-grid');
 * ```
 *
 * ## Configuration Architecture
 *
 * The grid follows a **single source of truth** pattern where all configuration
 * is managed by ConfigManager. Users can set configuration via multiple inputs:
 *
 * **Input Sources (precedence low → high):**
 * 1. `gridConfig` property - base configuration object
 * 2. Light DOM elements:
 *    - `<tbw-grid-column>` → `effectiveConfig.columns`
 *    - `<tbw-grid-header title="...">` → `effectiveConfig.shell.header.title`
 *    - `<tbw-grid-header-content>` → `effectiveConfig.shell.header.content`
 * 3. `columns` property → merged into `effectiveConfig.columns`
 * 4. `fitMode` property → merged into `effectiveConfig.fitMode`
 * 5. Column inference from first row (if no columns defined)
 *
 * **Derived State:**
 * - `_columns` - processed columns from `effectiveConfig.columns` after plugin hooks
 * - `_rows` - processed rows after plugin hooks (grouping, filtering, etc.)
 *
 * ConfigManager.merge() is the single place where all inputs converge.
 * All rendering and logic should read from `effectiveConfig` or derived state.
 *
 * @element tbw-grid
 *
 * @csspart container - The main grid container
 * @csspart header - The header row container
 * @csspart body - The body/rows container
 *
 * @cssprop --tbw-color-bg - Background color
 * @cssprop --tbw-color-fg - Foreground/text color
 */
// Injected by Vite at build time from package.json
declare const __GRID_VERSION__: string;

/** @since 0.1.1 */
export class DataGridElement<T = any> extends HTMLElement implements InternalGrid<T> {
  // TODO: Rename to 'data-grid' when migration is complete
  static readonly tagName = GRID_TAG_NAME;
  /** Version of the grid component, injected at build time from package.json */
  static readonly version = typeof __GRID_VERSION__ !== 'undefined' ? __GRID_VERSION__ : 'dev';
  /**
   * Tag name this bundle should render and query for.
   *
   * In the common (single-version) case this equals {@link tagName}
   * (`'tbw-grid'`).
   *
   * Resolution rules (see `registerDataGrid()` at the bottom of this file):
   *  - **No existing registration** → this class registers under `tbw-grid`;
   *    `activeTag === 'tbw-grid'`.
   *  - **Existing registration at the SAME version** → this class is NOT
   *    redefined (the first-loaded bundle's class keeps the registration),
   *    and `activeTag` still points at `'tbw-grid'` so adapters render the
   *    shared element. Note that in this branch `customElements.get(activeTag)`
   *    returns the OTHER bundle's class, not this one.
   *  - **Existing registration at a DIFFERENT version** → this class
   *    registers under a version-suffixed tag like `tbw-grid-v2-14-0` and
   *    `activeTag` reflects that suffixed name; the two implementations
   *    coexist on the page.
   *
   * Adapters and consumers that need to construct or query for the grid
   * element MUST use this property, not the hard-coded `tagName` literal.
   *
   * Backed by `internal/tag-registry` (a leaf module) so `style-injector` and
   * other internals can read the active tag without importing this class.
   */
  static get activeTag(): string {
    return getActiveGridTag();
  }
  static set activeTag(tag: string) {
    setActiveGridTag(tag);
  }

  /** Static counter for generating unique grid IDs */
  static #instanceCounter = 0;

  // #region Static Methods - Framework Adapters
  /**
   * Registry of framework adapters that handle converting light DOM elements
   * to functional renderers/editors. Framework libraries (Angular, React, Vue)
   * register adapters to enable zero-boilerplate component integration.
   */
  private static adapters: FrameworkAdapter[] = [];

  /**
   * Register a framework adapter for handling framework-specific components.
   * Adapters are checked in registration order when processing light DOM templates.
   *
   * @example
   * ```typescript
   * // In @toolbox-web/grid-angular
   * import { AngularGridAdapter } from '@toolbox-web/grid-angular';
   *
   * // One-time setup in app
   * GridElement.registerAdapter(new AngularGridAdapter(injector, appRef));
   * ```
   * @category Framework Adapters
   */
  static registerAdapter(adapter: FrameworkAdapter): void {
    this.adapters.push(adapter);
  }

  /**
   * Get all registered framework adapters.
   * Used internally by light DOM parsing to find adapters that can handle templates.
   * @category Framework Adapters
   */
  static getAdapters(): readonly FrameworkAdapter[] {
    return this.adapters;
  }

  /**
   * Clear all registered adapters (primarily for testing).
   * @category Framework Adapters
   */
  static clearAdapters(): void {
    this.adapters = [];
  }
  // #endregion

  // #region Static Methods - Observed Attributes
  /** @internal Web component lifecycle - not part of public API */
  static get observedAttributes(): string[] {
    return ['rows', 'columns', 'grid-config', 'fit-mode', 'column-inference', 'loading'];
  }
  // #endregion

  /**
   * The render root for the grid. Without Shadow DOM, this is the element itself.
   * This abstraction allows internal code to work the same way regardless of DOM mode.
   */
  get #renderRoot(): HTMLElement {
    return this;
  }

  #initialized = false;

  // Ready Promise
  #readyPromise: Promise<void>;
  #readyResolve?: () => void;

  // #region Input Properties
  // Raw rows are stored here. Config sources (gridConfig, columns, fitMode)
  // are owned by ConfigManager. Grid.ts property setters delegate to ConfigManager.
  #rows: T[] = [];
  // #endregion

  // #region Private properties
  // effectiveConfig is owned by ConfigManager - access via getter
  get #effectiveConfig(): GridConfig<T> {
    return this.#configManager?.effective ?? {};
  }

  #connected = false;

  // Batched Updates - coalesces rapid property changes into single update
  #pendingUpdate = false;
  #pendingUpdateFlags = {
    rows: false,
    columns: false,
    gridConfig: false,
    fitMode: false,
    columnInference: false,
  };

  // Render Scheduler - centralizes all rendering through RAF
  #scheduler!: RenderScheduler;

  #scrollRaf = 0;
  #pendingScrollTop: number | null = null;
  #hScrollRaf = 0;
  #hasScrollPlugins = false; // Cached flag for plugin scroll handlers
  #scrollMeasureTimeout = 0; // Debounce timer for measuring rows after scroll settles
  #renderRowHook?: (row: any, rowEl: HTMLElement, rowIndex: number) => boolean; // Cached hook to avoid closures
  #touchState: TouchScrollState = createTouchScrollState();
  #eventAbortController?: AbortController;
  #resizeObserver?: ResizeObserver;
  #idleCallbackHandle?: number; // Handle for cancelling deferred idle work

  // Pooled scroll event object (reused to avoid GC pressure during scroll)
  #pooledScrollEvent: ScrollEvent = {
    scrollTop: 0,
    scrollLeft: 0,
    scrollHeight: 0,
    scrollWidth: 0,
    clientHeight: 0,
    clientWidth: 0,
  };

  // Plugin System
  #pluginManager!: PluginManager;
  #lastPluginsArray?: BaseGridPlugin[]; // Track last attached plugins to avoid unnecessary re-initialization
  #lastFeaturesConfig?: Record<string, unknown>; // Track last features config for change detection

  // Virtualization manager — owns VirtualState + all virtualization methods
  #virtManager!: VirtualizationManager<T>;

  // Focus manager — owns focus/navigation state and external focus containers
  #focusManager!: FocusManager<T>;

  // Row manager — owns row CRUD operations (updateRow, insertRow, removeRow, etc.)
  #rowManager!: RowManager<T>;

  /**
   * Exposes plugin manager for event bus operations (subscribe/unsubscribe/emit).
   * Plugins access this via `this.grid._pluginManager` in `BaseGridPlugin.on/off/emitPluginEvent`.
   * @internal
   */
  get _pluginManager(): PluginManager | undefined {
    return this.#pluginManager;
  }

  // Event Listeners
  #eventListenersAdded = false; // Guard against adding duplicate component-level listeners
  #scrollAbortController?: AbortController; // Separate controller for DOM scroll listeners (recreated on DOM changes)
  #scrollAreaEl?: HTMLElement; // Reference to horizontal scroll container (.tbw-scroll-area)

  // Column State
  #initialColumnState?: GridColumnState;

  // Config Manager
  #configManager!: ConfigManager<T>;

  // FEATURE-INSTANCE-GATE-370: per-grid cache of feature-derived plugin
  // instances, keyed by plugin name. A feature is instantiated at most once per
  // grid: on every `#initializePlugins` re-resolution the cached instance is
  // reused (its config refreshed from the freshly-resolved one) instead of
  // constructing a second instance. This keeps each plugin's identity — and
  // therefore its accumulated state (e.g. the shell's API/light-DOM tool
  // panels) — stable across feature/plugin config changes. Without this gate
  // the auto-registered shell instance was silently replaced by a fresh feature
  // instance, dropping API-registered tool panels (regression after #370).
  #featureInstances = new Map<string, BaseGridPlugin>();

  // Loading State
  #loading = false;
  #loadingRows = new Set<string>(); // Row IDs currently loading
  #loadingCells = new Map<string, Set<string>>(); // Map<rowId, Set<field>> for cells loading
  #loadingOverlayEl?: HTMLElement; // Cached loading overlay element
  // Empty-state overlay state — element + last-applied context — kept inside
  // a single struct managed by `updateEmptyOverlay()` in `internal/empty.ts`.
  #emptyOverlay: EmptyOverlayState = {};

  // Row ID Map - O(1) lookup for rows by ID
  #rowIdMap = new Map<string, { row: T; index: number }>();
  #rowIdMapDirty = false;
  // #endregion

  // #region Derived State
  // _rows: result of applying plugin processRows hooks.
  // Accessor so EVERY reassignment (sort fast path, clear-sort, RowDragDrop, …) marks the
  // lazy row-ID map dirty — otherwise `_getRowEntry(id).index` goes stale (#526).
  #processedRows: T[] = [];
  get _rows(): T[] {
    return this.#processedRows;
  }
  set _rows(value: T[]) {
    this.#processedRows = value;
    this.#rowIdMapDirty = true;
  }

  // _baseColumns: columns before plugin transformation (analogous to #rows for row processing)
  // This is the source of truth for processColumns - plugins transform these
  #baseColumns: ColumnInternal<T>[] = [];

  // _columns is a getter/setter that operates on effectiveConfig.columns
  // This ensures effectiveConfig.columns is the single source of truth for columns
  // _columns always contains ALL columns (including hidden)
  get _columns(): ColumnInternal<T>[] {
    return (this.#effectiveConfig.columns ?? []) as ColumnInternal<T>[];
  }
  set _columns(value: ColumnInternal<T>[]) {
    this.#effectiveConfig.columns = value as ColumnConfig<T>[];
    this.#visibleColumnsCache = undefined;
  }

  // visibleColumns returns only visible columns for rendering
  // This is what header/row rendering should use
  // Cached — invalidated when _columns is set or _invalidateVisibleColumnsCache() is called
  #visibleColumnsCache?: ColumnInternal<T>[];
  get _visibleColumns(): ColumnInternal<T>[] {
    return (this.#visibleColumnsCache ??= this._columns.filter((c) => !c.hidden));
  }

  /**
   * Clear the cached _visibleColumns so the next access recomputes from _columns.
   * Called by ConfigManager when it mutates effectiveConfig.columns directly
   * (bypassing the _columns setter which normally handles invalidation).
   * @internal
   */
  _invalidateVisibleColumnsCache(): void {
    this.#visibleColumnsCache = undefined;
  }
  // #endregion

  // #region Runtime State (Plugin-accessible)
  // DOM references
  _headerRowEl!: HTMLElement;
  _bodyEl!: HTMLElement;
  _rowPool: RowElementInternal[] = [];
  _resizeController!: ResizeController;

  // Virtualization — delegated to VirtualizationManager, exposed via getter for plugin access
  get _virtualization(): VirtualState {
    return this.#virtManager.state;
  }
  set _virtualization(value: VirtualState) {
    // Support test mocking — merge incoming partial state into the live state object
    Object.assign(this.#virtManager.state, value);
  }

  // Focus & navigation
  _focusRow = 0;
  _focusCol = 0;
  /** Flag to restore focus styling after next render. @internal */
  _restoreFocusAfterRender = false;

  // Sort state
  _sortState: { field: string; direction: 1 | -1 } | null = null;

  // Layout
  _gridTemplate = '';
  // #endregion

  // #region Implementation Details (Internal only)
  __rowRenderEpoch = 0;
  __didInitialAutoSize = false;

  /** Light DOM columns cache - delegates to ConfigManager */
  get __lightDomColumnsCache(): ColumnInternal[] | undefined {
    return this.#configManager?.lightDomColumnsCache as ColumnInternal[] | undefined;
  }
  set __lightDomColumnsCache(value: ColumnInternal[] | undefined) {
    if (this.#configManager) {
      this.#configManager.lightDomColumnsCache = value as ColumnInternal<T>[] | undefined;
    }
  }

  /** Original column nodes - delegates to ConfigManager */
  get __originalColumnNodes(): HTMLElement[] | undefined {
    return this.#configManager?.originalColumnNodes;
  }
  set __originalColumnNodes(value: HTMLElement[] | undefined) {
    if (this.#configManager) {
      this.#configManager.originalColumnNodes = value;
    }
  }

  __originalOrder: T[] = [];

  /**
   * Framework adapter instance set by framework directives (Angular Grid, React DataGrid).
   * Used to handle framework-specific component rendering.
   * @internal
   */
  __frameworkAdapter?: FrameworkAdapter;

  // Cached DOM refs for hot path (refreshVirtualWindow) - avoid querySelector per scroll
  __rowsBodyEl: HTMLElement | null = null;
  // #endregion

  // #region Public API Props (getters/setters)
  // Getters return the EFFECTIVE value (after merging), not the raw input.
  // This is what consumers and plugins need - the current resolved state.
  // Setters update input properties which trigger re-merge into effectiveConfig.

  /**
   * Get or set the row data displayed in the grid.
   *
   * The getter returns processed rows (after filtering, sorting, grouping by plugins).
   * The setter accepts new source data and triggers a re-render.
   *
   * @group Configuration
   * @example
   * ```typescript
   * // Set initial data
   * grid.rows = employees;
   *
   * // Update with new data (triggers re-render)
   * grid.rows = [...employees, newEmployee];
   *
   * // Read current (processed) rows
   * console.log(`Displaying ${grid.rows.length} rows`);
   * ```
   */
  get rows(): T[] {
    return this._rows;
  }
  set rows(value: T[]) {
    // Invariant: #rows is always an array. Coerce nullish/non-array to []
    // (frameworks may sync `grid.rows = undefined` when no rows prop is provided).
    const next = Array.isArray(value) ? value : [];
    const oldValue = this.#rows;
    this.#rows = next;
    if (oldValue !== next) {
      this.#queueUpdate('rows');
    }
  }

  /**
   * Get the original unfiltered/unprocessed source rows.
   *
   * Use this when you need access to all source data regardless of active
   * filters, sorting, or grouping applied by plugins. The `rows` property
   * returns processed data, while `sourceRows` returns the original input.
   *
   * @group Configuration
   * @example
   * ```typescript
   * // Get total count including filtered-out rows
   * console.log(`${grid.rows.length} of ${grid.sourceRows.length} rows visible`);
   *
   * // Export all data, not just visible
   * exportToCSV(grid.sourceRows);
   * ```
   */
  get sourceRows(): T[] {
    return this.#rows;
  }

  /** @internal Used by RowManager for insertRow/removeRow mutations. */
  set sourceRows(rows: T[]) {
    this.#rows = Array.isArray(rows) ? rows : [];
  }

  /**
   * Get or set the column configurations.
   *
   * The getter returns processed columns (after plugin transformations).
   * The setter accepts an array of column configs or a column config map.
   *
   * @group Configuration
   * @example
   * ```typescript
   * // Set columns as array
   * grid.columns = [
   *   { field: 'name', header: 'Name', width: 200 },
   *   { field: 'email', header: 'Email' },
   *   { field: 'role', header: 'Role', width: 120 }
   * ];
   *
   * // Set columns as map (keyed by field)
   * grid.columns = {
   *   name: { header: 'Name', width: 200 },
   *   email: { header: 'Email' },
   *   role: { header: 'Role', width: 120 }
   * };
   *
   * // Read current columns
   * grid.columns.forEach(col => {
   *   console.log(`${col.field}: ${col.width ?? 'auto'}`);
   * });
   * ```
   */
  get columns(): ColumnConfig<T>[] {
    return [...this._columns] as ColumnConfig<T>[];
  }
  set columns(value: ColumnConfig<T>[] | ColumnShorthand<T>[] | ColumnConfigMap<T> | undefined) {
    // Expand shorthand strings (e.g. `['id:number', 'name']`) so the JSON
    // `columns` attribute and JS assignment both accept the friendly form (#276).
    const normalized = Array.isArray(value) ? normalizeColumns<T>(value) : value;
    const oldValue = this.#configManager?.getColumns();
    this.#configManager?.setColumns(normalized);
    // Compare against `normalized` (what was actually stored), not the raw
    // `value` — `normalizeColumns()` returns a fresh array, so a re-render is
    // scheduled whenever the stored reference changes (#276 review).
    if (oldValue !== normalized) {
      this.#queueUpdate('columns');
    }
  }

  /**
   * Get or set the full grid configuration object.
   *
   * The getter returns the effective (merged) configuration.
   * The setter accepts a new configuration and triggers a full re-render.
   *
   * @group Configuration
   * @example
   * ```typescript
   * import { SelectionPlugin, SortingPlugin } from '@toolbox-web/grid/all';
   *
   * // Set full configuration
   * grid.gridConfig = {
   *   columns: [
   *     { field: 'name', header: 'Name' },
   *     { field: 'status', header: 'Status' }
   *   ],
   *   fitMode: 'stretch',
   *   plugins: [
   *     new SelectionPlugin({ mode: 'row' }),
   *     new SortingPlugin()
   *   ]
   * };
   *
   * // Read current configuration
   * console.log('Fit mode:', grid.gridConfig.fitMode);
   * console.log('Columns:', grid.gridConfig.columns?.length);
   * ```
   */
  get gridConfig(): GridConfig<T> {
    return this.#effectiveConfig;
  }
  set gridConfig(value: GridConfig<T> | undefined) {
    // Let the framework adapter pre-process the config (convert component
    // classes / VNodes / JSX to DOM-returning functions) before the grid sees it.
    if (value && this.__frameworkAdapter?.processConfig) {
      value = this.__frameworkAdapter.processConfig(value);
    }
    const oldValue = this.#configManager?.getGridConfig();
    this.#configManager?.setGridConfig(value);
    if (oldValue !== value) {
      // Clear light DOM column cache so columns are re-parsed from light DOM
      // This is needed for frameworks like Angular that project content asynchronously
      this.#configManager.clearLightDomCache();
      this.#queueUpdate('gridConfig');
    }
  }

  /**
   * @internal Plugin API - the original, unmerged source config as supplied via
   * the `gridConfig` property. Plugins read this (not {@link gridConfig}, which
   * returns the merged result) to distinguish caller-supplied values from
   * plugin/API-contributed ones.
   */
  get _sourceConfig(): GridConfig<T> | undefined {
    return this.#configManager?.getGridConfig();
  }

  /**
   * Get or set the column sizing mode.
   *
   * - `'stretch'` (default): Columns stretch to fill available width
   * - `'fixed'`: Columns use explicit widths; horizontal scroll if needed
   * - `'auto'`: Columns auto-size to content on initial render
   *
   * @group Configuration
   * @example
   * ```typescript
   * // Use fixed widths with horizontal scroll
   * grid.fitMode = 'fixed';
   *
   * // Stretch columns to fill the container (default)
   * grid.fitMode = 'stretch';
   * ```
   */
  get fitMode(): FitMode {
    return this.#effectiveConfig.fitMode ?? 'stretch';
  }
  set fitMode(value: FitMode | undefined) {
    const oldValue = this.#configManager?.getFitMode();
    this.#configManager?.setFitMode(value);
    if (oldValue !== value) {
      this.#queueUpdate('fitMode');
    }
  }

  /**
   * Get or set how automatic column inference combines with explicitly provided columns.
   *
   * - `'auto'` (default): infer columns only when none are provided. Declaring a
   *   single column disables inference and renders only the declared column(s).
   * - `'merge'`: always infer the full column set from the data (data-key order),
   *   then overlay provided columns matched by `field`. A provided column customizes
   *   only its own field and keeps its data position; provided columns for fields
   *   absent from the data are appended as computed columns.
   *
   * Can also be set via `gridConfig.columnInference` or the `column-inference` attribute.
   *
   * @group Configuration
   * @since 2.17.0
   * @example
   * ```typescript
   * // Render every data field, then customize just one column
   * grid.columnInference = 'merge';
   * grid.columns = [{ field: 'salary', type: 'number', header: 'Salary (USD)' }];
   * ```
   */
  get columnInference(): ColumnInferenceMode {
    return this.#effectiveConfig.columnInference ?? 'auto';
  }
  set columnInference(value: ColumnInferenceMode | undefined) {
    const oldValue = this.#configManager?.getColumnInference();
    this.#configManager?.setColumnInference(value);
    if (oldValue !== value) {
      this.#queueUpdate('columnInference');
    }
  }

  // #endregion

  // #region Loading API

  /**
   * Whether the grid is currently in a loading state.
   * When true, displays a loading overlay with spinner (or custom loadingRenderer).
   *
   * @example
   * ```typescript
   * grid.loading = true;
   * const data = await fetchData();
   * grid.rows = data;
   * grid.loading = false;
   * ```
   */
  get loading(): boolean {
    return this.#loading;
  }

  set loading(value: boolean) {
    const wasLoading = this.#loading;
    this.#loading = value;

    // Only mutate attributes on an actual state transition. This keeps repeated
    // `loading=true` calls (overlapping async work) idempotent, and prevents
    // a no-op `loading=false` from clobbering an `aria-busy` value that another
    // owner (e.g. FilteringPlugin's async filterHandler) has set on the host.
    if (wasLoading === value) return;

    if (value) {
      this.setAttribute('loading', '');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
    }

    this.#updateLoadingOverlay();
    // Loading and empty overlays are mutually exclusive: hide empty when
    // loading turns on; reconsider it when loading turns off (rows may have
    // arrived empty).
    this.#updateEmptyOverlay();
  }

  /**
   * Set loading state for a specific row.
   * Shows a small spinner or custom loadingRenderer on the row.
   *
   * @param rowId - The row's unique identifier (from getRowId)
   * @param loading - Whether the row is loading
   *
   * @example
   * ```typescript
   * // Show loading while saving row data
   * grid.setRowLoading('row-123', true);
   * await saveRow(rowData);
   * grid.setRowLoading('row-123', false);
   * ```
   */
  setRowLoading(rowId: string, loading: boolean): void {
    const wasLoading = this.#loadingRows.has(rowId);
    if (loading) {
      this.#loadingRows.add(rowId);
    } else {
      this.#loadingRows.delete(rowId);
    }

    // Update row element if state changed
    if (wasLoading !== loading) {
      this.#updateRowLoadingState(rowId, loading);
    }
  }

  /**
   * Set loading state for a specific cell.
   * Shows a small spinner or custom loadingRenderer on the cell.
   *
   * @param rowId - The row's unique identifier
   * @param field - The column field
   * @param loading - Whether the cell is loading
   *
   * @example
   * ```typescript
   * // Show loading while validating a single field
   * grid.setCellLoading('row-123', 'email', true);
   * const valid = await validateEmail(newValue);
   * grid.setCellLoading('row-123', 'email', false);
   * ```
   */
  setCellLoading(rowId: string, field: string, loading: boolean): void {
    let cellFields = this.#loadingCells.get(rowId);
    const wasLoading = cellFields?.has(field) ?? false;

    if (loading) {
      if (!cellFields) {
        cellFields = new Set();
        this.#loadingCells.set(rowId, cellFields);
      }
      cellFields.add(field);
    } else {
      cellFields?.delete(field);
      // Clean up empty sets
      if (cellFields?.size === 0) {
        this.#loadingCells.delete(rowId);
      }
    }

    // Update cell element if state changed
    if (wasLoading !== loading) {
      this.#updateCellLoadingState(rowId, field, loading);
    }
  }

  /**
   * Check if a row is currently in loading state.
   * @param rowId - The row's unique identifier
   */
  isRowLoading(rowId: string): boolean {
    return this.#loadingRows.has(rowId);
  }

  /**
   * Check if a cell is currently in loading state.
   * @param rowId - The row's unique identifier
   * @param field - The column field
   */
  isCellLoading(rowId: string, field: string): boolean {
    return this.#loadingCells.get(rowId)?.has(field) ?? false;
  }

  /**
   * Clear all row and cell loading states.
   */
  clearAllLoading(): void {
    this.loading = false;

    // Clear all row loading states
    for (const rowId of this.#loadingRows) {
      this.#updateRowLoadingState(rowId, false);
    }
    this.#loadingRows.clear();

    // Clear all cell loading states
    for (const [rowId, fields] of this.#loadingCells) {
      for (const field of fields) {
        this.#updateCellLoadingState(rowId, field, false);
      }
    }
    this.#loadingCells.clear();
  }

  // #endregion

  // #region State Access (internal plugin API)

  /**
   * Effective config accessor for internal modules and plugins.
   * Returns the merged config (single source of truth) before plugin processing.
   * Use this when you need the raw merged config (e.g., for column definitions including hidden).
   * @group State Access
   * @internal Plugin API
   */
  get effectiveConfig(): GridConfig<T> {
    return this.#effectiveConfig;
  }

  /**
   * Get the disconnect signal for event listener cleanup.
   * This signal is aborted when the grid disconnects from the DOM.
   * Plugins and internal code can use this for automatic listener cleanup.
   * @group State Access
   * @internal Plugin API
   * @example
   * element.addEventListener('click', handler, { signal: this.grid.disconnectSignal });
   */
  get disconnectSignal(): AbortSignal {
    // Ensure AbortController exists (created in connectedCallback before plugins attach)
    if (!this.#eventAbortController) {
      this.#eventAbortController = new AbortController();
    }
    return this.#eventAbortController.signal;
  }
  // #endregion

  /**
   * @internal Do not call directly. Use `createGrid()` or `document.createElement('tbw-grid')`.
   */
  constructor() {
    super();
    // No Shadow DOM - render directly into the element
    void this.#injectStyles(); // Fire and forget - styles load asynchronously
    this.#readyPromise = new Promise((res) => (this.#readyResolve = res));

    // Initialize virtualization manager (tightly coupled — reads grid state directly)
    this.#virtManager = new VirtualizationManager<T>(this);

    // Initialize focus manager (tightly coupled — reads grid state directly)
    this.#focusManager = new FocusManager<T>(this);

    // Initialize row manager (tightly coupled — reads grid state directly)
    this.#rowManager = new RowManager<T>(this);

    // Initialize render scheduler (tightly coupled — calls grid pipeline methods directly)
    this.#scheduler = new RenderScheduler(this);
    // Connect ready promise to scheduler
    this.#scheduler.setInitialReadyResolver(() => this.#readyResolve?.());

    // Initialize config manager (reads directly from grid internals)
    this.#configManager = new ConfigManager<T>(this);
  }

  /**
   * Inject grid styles into the document.
   * Delegates to the style-injector module (singleton pattern).
   */
  async #injectStyles(): Promise<void> {
    await injectStyles(styles);
  }

  // #region Plugin System
  /**
   * Get a plugin instance by its class constructor.
   *
   * **Prefer {@link getPluginByName}** for most use cases — it avoids importing the plugin class
   * and returns the actual instance registered in the grid.
   *
   * @example
   * ```ts
   * // Preferred: by name (no import needed)
   * const selection = grid.getPluginByName('selection');
   *
   * // Alternative: by class (requires import)
   * import { SelectionPlugin } from '@toolbox-web/grid/plugins/selection';
   * const selection = grid.getPlugin(SelectionPlugin);
   * selection?.selectAll();
   * ```
   *
   * @param PluginClass - The plugin class (constructor) to look up.
   * @returns The plugin instance, or `undefined` if not registered.
   * @group Plugin Communication
   */
  getPlugin<P>(PluginClass: new (...args: any[]) => P): P | undefined {
    return this.#pluginManager?.getPlugin(PluginClass as new (...args: any[]) => BaseGridPlugin) as P | undefined;
  }

  /**
   * Get a plugin instance by its string name.
   * Useful for loose coupling when you don't want to import the plugin class
   * (e.g., in framework adapters or dynamic scenarios).
   *
   * @example
   * ```ts
   * const editing = grid.getPluginByName('editing');
   * ```
   *
   * @param name - The plugin name (matches `BaseGridPlugin.name`).
   * @returns The plugin instance, or `undefined` if not registered.
   * @group Plugin Communication
   */
  getPluginByName<K extends string>(
    name: K,
  ): (K extends keyof PluginNameMap ? PluginNameMap[K] : BaseGridPlugin) | undefined {
    return this.#pluginManager?.getPluginByName(name) as any;
  }

  /**
   * Request a full re-render of the grid.
   * Called by plugins when they need the grid to update.
   * Note: This does NOT reset plugin state - just re-processes rows/columns and renders.
   *
   * **Use this for row-data updates** (lazy page loads, model changes). Triggers
   * `RenderPhase.ROWS` (processRows + virtualization). Prefer this over
   * {@link requestColumnsRender} unless column **definitions** actually change —
   * `requestColumnsRender` runs the full pipeline (header rebuild + processColumns)
   * which can change scroll height and create scroll → render feedback loops if
   * used for incremental data updates.
   * @group Rendering
   * @internal Plugin API
   */
  requestRender(): void {
    this.#scheduler.requestPhase(RenderPhase.ROWS, 'plugin:requestRender');
  }

  /**
   * Request a columns re-render of the grid.
   * Called by plugins when they need to trigger processColumns hooks.
   * This uses a higher render phase than requestRender() to ensure
   * column processing occurs.
   *
   * **Only call this when column definitions change** (visibility, pin, group,
   * width, order). For row-data updates use {@link requestRender} — this phase
   * adds processColumns + header rebuild and rebuilds the full pipeline,
   * which is wasted work for row-only changes and can perturb scroll height.
   * @group Rendering
   * @internal Plugin API
   */
  requestColumnsRender(): void {
    this.#scheduler.requestPhase(RenderPhase.COLUMNS, 'plugin:requestColumnsRender');
  }

  /**
   * Request a full re-render and restore focus styling afterward.
   * Use this when a plugin action (like expand/collapse) triggers a render
   * but needs to maintain keyboard navigation focus.
   * @group Rendering
   * @internal Plugin API
   */
  requestRenderWithFocus(): void {
    this._restoreFocusAfterRender = true;
    this.#scheduler.requestPhase(RenderPhase.ROWS, 'plugin:requestRenderWithFocus');
  }

  /**
   * Update the grid's column template CSS.
   * Called by resize controller during column resize operations.
   * @group Rendering
   * @internal Plugin API
   */
  updateTemplate(): void {
    updateTemplate(this);
  }

  /**
   * Request a lightweight style update without rebuilding DOM.
   * Called by plugins when they only need to update CSS classes/styles.
   * This runs all plugin afterRender hooks without rebuilding row/column DOM.
   * @group Rendering
   * @internal Plugin API
   */
  requestAfterRender(): void {
    this.#scheduler.requestPhase(RenderPhase.STYLE, 'plugin:requestAfterRender');
  }

  /**
   * Re-render visible rows without rebuilding the row model or recalculating geometry.
   * Uses non-force refreshVirtualWindow to avoid spacer height recalculations that
   * can cause scroll position oscillation. Useful when row data has been updated in-place
   * (e.g., server-side block loads replacing placeholders with real data).
   * @group Rendering
   * @internal Plugin API
   */
  requestVirtualRefresh(): void {
    // Rows were swapped in place (e.g. ServerSide placeholders → loaded rows), so
    // ids in a row-ID map built before the swap are stale. Mark it dirty — O(1);
    // the lazy rebuild runs only if something reads it.
    this.#rowIdMapDirty = true;
    // Invalidate start so refreshVirtualWindow doesn't early-exit
    this._virtualization.start = -1;
    // The rebuilt rows lost plugin visual state (selection classes, aria-selected);
    // the virtualized non-force path runs no afterRender, so reapply it exactly as
    // a scroll that changes the window does (#onScrollBatched).
    if (this.refreshVirtualWindow(false)) {
      this.#pluginManager?.onScrollRender();
    }
  }

  /**
   * Initialize plugin system with instances from config.
   * Plugins are class instances passed in gridConfig.plugins[].
   * If gridConfig.features is set and the feature registry is loaded,
   * feature-derived plugins are prepended before explicit plugins.
   */
  #initializePlugins(): void {
    // Create plugin manager for this grid
    this.#pluginManager = new PluginManager(this);

    // Get plugin instances from config - ensure it's an array
    const pluginsConfig = this.#effectiveConfig?.plugins;
    const explicitPlugins = Array.isArray(pluginsConfig) ? (pluginsConfig as BaseGridPlugin[]) : [];

    // Resolve feature-derived plugins (if feature registry is loaded)
    const features = this.#effectiveConfig?.features;
    let featurePlugins: BaseGridPlugin[] = [];
    if (features && resolveFeatures) {
      const resolved = resolveFeatures(features as Record<string, unknown>) as BaseGridPlugin[];
      // FEATURE-INSTANCE-GATE-370: reuse the cached instance for each feature
      // (refreshing its config) so a feature is never instantiated twice for
      // this grid; only genuinely new features create a new instance.
      featurePlugins = resolved.map((p) => this.#gateFeatureInstance(p));
    }

    // Merge: feature-derived first (for dependency ordering), then explicit plugins.
    // Manual `plugins` take precedence on name conflict — drop the feature-derived
    // instance so we don't double-attach (which would log TBW023). This matches
    // the contract documented on `GridConfig.features`.
    let allPlugins: BaseGridPlugin[];
    if (featurePlugins.length === 0) {
      allPlugins = explicitPlugins;
    } else if (explicitPlugins.length === 0) {
      allPlugins = featurePlugins;
    } else {
      const explicitNames = new Set(explicitPlugins.map((p) => p.name));
      const dedupedFeaturePlugins = featurePlugins.filter((p) => !explicitNames.has(p.name));
      allPlugins = [...dedupedFeaturePlugins, ...explicitPlugins];
    }

    // Attach all plugins. The shell is opt-in (extraction #370): when present
    // it resolves from `features: { shell }` or `plugins: [ShellPlugin]` like
    // any other plugin and self-activates on attach — core never constructs or
    // registers it. Provided dependencies go through the same per-grid gate.
    this.#pluginManager.attachAll(allPlugins, (p) => this.#gateFeatureInstance(p));
  }

  /**
   * FEATURE-INSTANCE-GATE-370: return the canonical per-grid instance for a
   * freshly-resolved feature plugin. If this grid already has an instance for
   * the feature's name, reuse it (refreshing its config from the fresh one) so
   * the feature is instantiated at most once per grid and its accumulated state
   * survives. Otherwise cache and return the fresh instance.
   */
  #gateFeatureInstance(fresh: BaseGridPlugin): BaseGridPlugin {
    const cached = this.#featureInstances.get(fresh.name);
    if (cached && cached !== fresh) {
      cached.refreshUserConfigFrom(fresh);
      return cached;
    }
    this.#featureInstances.set(fresh.name, fresh);
    return fresh;
  }

  /**
   * Inject all plugin styles into the consolidated style element.
   * Plugin styles are appended after base grid styles in the same <style> element.
   * Uses a Map to accumulate styles from all grid instances on the page.
   */
  #injectAllPluginStyles(): void {
    const pluginStyles = this.#pluginManager?.getPluginStyles() ?? [];
    addPluginStyles(pluginStyles);
  }

  /**
   * Update plugins when grid config changes.
   * With class-based plugins, we need to detach old and attach new.
   * Skips re-initialization if the plugins array and features config haven't changed.
   */
  #updatePluginConfigs(): void {
    // Get the new plugins array from config
    const pluginsConfig = this.#effectiveConfig?.plugins;
    const newPlugins = Array.isArray(pluginsConfig) ? (pluginsConfig as BaseGridPlugin[]) : [];
    const newFeatures = (this.#effectiveConfig?.features as Record<string, unknown>) ?? undefined;

    // Check if features config changed (by reference)
    const featuresChanged = newFeatures !== this.#lastFeaturesConfig;

    // Check if plugins have actually changed (same array reference or same contents)
    // This avoids unnecessary detach/attach cycles on every render
    const pluginsUnchanged =
      this.#lastPluginsArray === newPlugins ||
      (this.#lastPluginsArray !== undefined &&
        this.#lastPluginsArray.length === newPlugins.length &&
        this.#lastPluginsArray.every((p, i) => p === newPlugins[i]));

    if (pluginsUnchanged && !featuresChanged) {
      // Nothing changed - just update reference tracking
      this.#lastPluginsArray = newPlugins;
      return;
    }

    // Plugins have changed - detach old and attach new
    if (this.#pluginManager) {
      this.#pluginManager.detachAll();
    }

    this.#initializePlugins();
    this.#injectAllPluginStyles();

    // Track the new plugins array and features config
    this.#lastPluginsArray = newPlugins;
    this.#lastFeaturesConfig = newFeatures;

    // Re-check variable heights mode: a plugin with getRowHeight() may have been added
    // after initial setup (e.g., Angular/React set gridConfig asynchronously via effects).
    // Without this, variableHeights stays false and the scroll handler uses fixed-height math,
    // producing incorrect translateY when detail rows are expanded.
    this.#virtManager.configureVariableHeights();

    // Re-run the config merge so the ShellPlugin's `processConfig` re-collects
    // shell contributions from the freshly re-initialized plugin instances
    // (extraction #370 — collection is now plugin-owned).
    this.#configManager.markSourcesChanged();
    this.#configManager.merge();

    // Update cached scroll plugin flag and re-setup scroll listeners if needed
    // This ensures horizontal scroll listener is added when plugins with onScroll handlers are added
    const hadScrollPlugins = this.#hasScrollPlugins;
    this.#hasScrollPlugins = this.#pluginManager?.getAll().some((p) => p.onScroll) ?? false;

    // Re-setup scroll listeners if scroll plugins were added (flag changed from false to true)
    if (!hadScrollPlugins && this.#hasScrollPlugins) {
      const gridContent = this.#renderRoot.querySelector('.tbw-grid-content');
      const gridRoot = gridContent ?? this.#renderRoot.querySelector('.tbw-grid-root');
      this.#setupScrollListeners(gridRoot);
    }
  }

  /**
   * Clean up plugin states when grid disconnects.
   */
  #destroyPlugins(): void {
    this.#pluginManager?.detachAll();
  }

  /**
   * Gets a renderer factory for tool panels from registered framework adapters.
   * Returns a factory function that tries each adapter in order until one handles the element.
   */
  #getToolPanelRendererFactory():
    ((element: HTMLElement) => ((container: HTMLElement) => void | (() => void)) | undefined) | undefined {
    const adapters = DataGridElement.getAdapters();
    if (adapters.length === 0 && !this.__frameworkAdapter) return undefined;

    // Also check for instance-level adapter (e.g., __frameworkAdapter from Angular Grid directive)
    const instanceAdapter = this.__frameworkAdapter;

    return (element: HTMLElement) => {
      // Try instance adapter first (from Grid directive)
      if (instanceAdapter?.createToolPanelRenderer) {
        const renderer = instanceAdapter.createToolPanelRenderer(element);
        if (renderer) return renderer;
      }

      // Try global adapters
      for (const adapter of adapters) {
        if (adapter.createToolPanelRenderer) {
          const renderer = adapter.createToolPanelRenderer(element);
          if (renderer) return renderer;
        }
      }

      return undefined;
    };
  }

  /**
   * @internal Plugin API - register a generic light-DOM mutation handler. The
   * observer is tag-agnostic core infrastructure; plugins (e.g. ShellPlugin)
   * declare which tags they watch and own the parsing logic.
   */
  _registerLightDomHandler(tagName: string, callback: () => void): void {
    this.#configManager.registerLightDomHandler(tagName, callback);
  }

  /** @internal Plugin API - unregister a light-DOM handler. */
  _unregisterLightDomHandler(tagName: string): void {
    this.#configManager.unregisterLightDomHandler(tagName);
  }

  /**
   * @internal Plugin API - re-merge config sources into `effectiveConfig`.
   * Plugins call this after re-parsing light DOM so the `processConfig` hook
   * folds the new values in.
   */
  _requestConfigMerge(): void {
    this.#configManager.markSourcesChanged();
    this.#configManager.merge();
  }

  /**
   * @internal Plugin API - build a renderer factory from registered framework
   * adapters. Generic adapter capability (not shell-specific).
   */
  _getToolPanelRendererFactory():
    ((element: HTMLElement) => ((container: HTMLElement) => void | (() => void)) | undefined) | undefined {
    return this.#getToolPanelRendererFactory();
  }
  // #endregion

  // #region Lifecycle
  /** @internal Web component lifecycle - not part of public API */
  connectedCallback(): void {
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
    if (!this.hasAttribute('version')) this.setAttribute('version', DataGridElement.version);
    // Tag-agnostic marker so adapters, themes, and CSS selectors can target
    // every grid instance regardless of whether it was registered under the
    // bare `tbw-grid` tag or a version-suffixed variant. See `activeTag`.
    if (!this.hasAttribute('data-tbw-grid')) this.setAttribute('data-tbw-grid', '');
    // Ensure grid has a unique ID for print isolation and other use cases
    if (!this.id) {
      this.id = `tbw-grid-${++DataGridElement.#instanceCounter}`;
    }
    this._rows = Array.isArray(this.#rows) ? [...this.#rows] : [];

    // Create AbortController for all event listeners (grid internal + plugins)
    // This must happen BEFORE plugins attach so they can use disconnectSignal
    // Abort any previous controller first (in case of re-connect)
    if (this.#eventAbortController) {
      this.#eventAbortController.abort();
      this.#eventListenersAdded = false; // Reset so listeners can be re-added
    }
    this.#eventAbortController = new AbortController();

    // Cancel any pending idle work from previous connection
    if (this.#idleCallbackHandle) {
      cancelIdle(this.#idleCallbackHandle);
      this.#idleCallbackHandle = undefined;
    }

    // === CRITICAL PATH (synchronous) - needed for first paint ===

    // Parse light DOM shell elements BEFORE merging config
    this.#parseLightDom();
    // Parse light DOM columns (must be before merge to pick up templates)
    this.#configManager.parseLightDomColumns(this);

    // Merge all config sources into effectiveConfig (including columns and shell)
    this.#configManager.merge();

    // Initialize plugin system (now plugins can access disconnectSignal)
    this.#initializePlugins();

    // Track the initial plugins array and features to avoid unnecessary re-initialization
    const pluginsConfig = this.#effectiveConfig?.plugins;
    this.#lastPluginsArray = Array.isArray(pluginsConfig) ? (pluginsConfig as BaseGridPlugin[]) : [];
    this.#lastFeaturesConfig = (this.#effectiveConfig?.features as Record<string, unknown>) ?? undefined;

    if (!this.#initialized) {
      this.#render();
      this.#injectAllPluginStyles(); // Inject plugin styles after render
      this.#initialized = true;
    }
    this.#afterConnect();

    // === DEFERRED WORK (idle) - not needed for first paint ===
    this.#idleCallbackHandle = scheduleIdle(
      () => {
        // Set up Light DOM observation via ConfigManager
        // This handles frameworks like Angular that project content asynchronously
        this.#setupLightDomHandlers();
      },
      { timeout: 100 },
    );
  }

  /** @internal Web component lifecycle - not part of public API */
  disconnectedCallback(): void {
    // Cancel any pending idle work
    if (this.#idleCallbackHandle) {
      cancelIdle(this.#idleCallbackHandle);
      this.#idleCallbackHandle = undefined;
    }

    // Cancel any pending scroll measurement
    if (this.#scrollMeasureTimeout) {
      clearTimeout(this.#scrollMeasureTimeout);
      this.#scrollMeasureTimeout = 0;
    }

    // Clean up plugin states
    this.#destroyPlugins();

    // Clean up shell state when an opt-in shell is attached (extraction #370).
    // Resolve by name (not class identity) so the feature-attached instance is
    // disposed even when its class differs from core's copy in the dist build
    // (#374). `disposeShellState` also tears down the shell's document-level
    // listeners. Loosely typed — core holds no ShellPlugin class reference.
    const shellPlugin = this.#pluginManager?.getPluginByName('shell') as { disposeShellState?(): void } | undefined;
    shellPlugin?.disposeShellState?.();

    // Cancel any ongoing touch momentum animation
    cancelMomentum(this.#touchState);

    // Abort all event listeners (internal + document-level)
    // This cleans up all listeners added with { signal } option
    if (this.#eventAbortController) {
      this.#eventAbortController.abort();
      this.#eventAbortController = undefined;
    }
    // Also abort scroll-specific listeners (separate controller)
    this.#scrollAbortController?.abort();
    this.#scrollAbortController = undefined;
    this.#eventListenersAdded = false; // Reset so listeners can be re-added on reconnect

    if (this._resizeController) {
      this._resizeController.dispose();
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = undefined;
    }
    this.#virtManager.disposeRowHeightObserver();

    // Clear caches to prevent memory leaks
    invalidateCellCache(this);
    this.#customStyleSheets.clear();
    this._virtualization.heightCache?.byKey.clear();

    // Clear plugin tracking to allow fresh initialization on reconnect
    this.#lastPluginsArray = undefined;
    this.#lastFeaturesConfig = undefined;

    // Clear row pool - detach from DOM and release references
    for (let i = 0; i < this._rowPool.length; i++) {
      this._rowPool[i].remove();
    }
    this._rowPool.length = 0;

    // Clear cached DOM refs to prevent stale references
    this.__rowsBodyEl = null;

    this.#connected = false;
  }

  /**
   * Handle HTML attribute changes.
   * Only processes attribute values when SET (non-null).
   * Removing an attribute does NOT clear JS-set properties.
   * @internal Web component lifecycle - not part of public API
   */
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    // Handle boolean attributes first (presence = true, absence/null = false, "false" = false)
    if (name === 'loading') {
      const isLoading = newValue !== null && newValue !== 'false';
      if (this.loading !== isLoading) {
        this.loading = isLoading;
      }
      return;
    }

    if (oldValue === newValue || !newValue || newValue === 'null' || newValue === 'undefined') return;

    // JSON attributes need parsing
    if (name === 'rows' || name === 'columns' || name === 'grid-config') {
      try {
        const parsed = JSON.parse(newValue);
        if (name === 'rows') this.rows = parsed;
        else if (name === 'columns') this.columns = parsed;
        else if (name === 'grid-config') this.gridConfig = parsed;
      } catch {
        warnDiagnostic(INVALID_ATTRIBUTE_JSON, `Invalid JSON for '${name}' attribute: ${newValue}`, this.id);
      }
    } else if (name === 'fit-mode') {
      this.fitMode = newValue as FitMode;
    } else if (name === 'column-inference') {
      this.columnInference = newValue as ColumnInferenceMode;
    }
  }

  /**
   * Re-resolve and cache the hot-path DOM references after a (re)build of the
   * grid structure. Shared by `#afterConnect()` and `#afterShellRefresh()`.
   *
   * @returns The grid root element the refs were resolved from, or `null` when
   *   the structure has not been rendered yet.
   */
  #cacheDomRefs(): Element | null {
    // Shell changes the DOM structure - need to find elements appropriately
    const gridContent = this.#renderRoot.querySelector('.tbw-grid-content');
    const gridRoot = gridContent ?? this.#renderRoot.querySelector('.tbw-grid-root');

    this._headerRowEl = gridRoot?.querySelector('.header-row') as HTMLElement;
    // Faux scrollbar pattern:
    // - .faux-vscroll-spacer sets virtual height
    // - .rows-viewport provides visible height for virtualization calculations
    this._virtualization.totalHeightEl = gridRoot?.querySelector('.faux-vscroll-spacer') as HTMLElement;
    this._virtualization.viewportEl = gridRoot?.querySelector('.rows-viewport') as HTMLElement;
    this._bodyEl = gridRoot?.querySelector('.rows') as HTMLElement;
    // Cache DOM refs for hot path (refreshVirtualWindow) - avoid querySelector per scroll
    this.__rowsBodyEl = gridRoot?.querySelector('.rows-body') as HTMLElement;

    return gridRoot;
  }

  #afterConnect(): void {
    const gridRoot = this.#cacheDomRefs();

    // Shell header content, custom toolbar buttons, default-open behavior, and
    // open-panel restoration are now rendered by the ShellPlugin in its
    // `afterStructuralRender` hook (fired from `#render()` just before this
    // method runs) — core holds no shell-content knowledge (extraction #370,
    // Task 1b, Step 1b.1.3).

    // Mark for tests that afterConnect ran
    this.setAttribute('data-upgraded', '');
    this.#connected = true;

    // Create resize controller BEFORE setup - renderHeader() needs it for resize handle mousedown events
    // (afterConnect can run more than once — tear the previous one down first)
    this._resizeController?.dispose();
    this._resizeController = createResizeController(this);

    // Run setup
    this.#setup();

    // Set up DOM-element scroll listeners (these need to be re-attached when DOM is recreated)
    this.#setupScrollListeners(gridRoot);

    // Only add component-level event listeners once (afterConnect can be called multiple times)
    // When shell state changes or refreshShellHeader is called, we re-run afterConnect
    // but component-level listeners should not be duplicated (they don't depend on DOM elements)
    // Scroll listeners are already set up above and handle DOM recreation via their own AbortController
    if (this.#eventListenersAdded) {
      return;
    }
    this.#eventListenersAdded = true;

    // Get the signal for event listener cleanup (AbortController created in connectedCallback)
    const signal = this.disconnectSignal;

    // Set up all root-level and document-level event listeners
    // Consolidates keydown, mousedown, mousemove, mouseup in one place (event-delegation.ts)
    setupRootEventDelegation(this, this, this.#renderRoot, signal);

    // Note: click/dblclick handlers are set up via setupCellEventDelegation in #setupScrollListeners
    // This consolidates all body-level delegated event handlers in one place (event-delegation.ts)

    // Configure variable row heights based on plugins and user config
    this.#virtManager.configureVariableHeights();

    // Initialize ARIA selection state
    queueMicrotask(() => this.#updateAriaSelection());

    // Request initial render through the scheduler.
    // The scheduler resolves ready() after the render cycle completes.
    // Framework adapters (React/Angular) will request COLUMNS phase via refreshColumns(),
    // which will be batched with this request - highest phase wins.
    this.#scheduler.requestPhase(RenderPhase.FULL, 'afterConnect');
  }

  /**
   * Set up scroll-related event listeners on DOM elements.
   * These need to be re-attached when the DOM is recreated (e.g., shell toggle).
   * Uses a separate AbortController that is recreated each time.
   */
  #setupScrollListeners(gridRoot: Element | null): void {
    // Abort any existing scroll listeners before adding new ones
    this.#scrollAbortController?.abort();
    this.#scrollAbortController = new AbortController();
    const scrollSignal = this.#scrollAbortController.signal;

    // Faux scrollbar pattern: scroll events come from the fake scrollbar element
    // Content area doesn't scroll - rows are positioned via transforms
    const fauxScrollbar = gridRoot?.querySelector('.faux-vscroll') as HTMLElement;
    const rowsEl = gridRoot?.querySelector('.rows') as HTMLElement;

    // Store reference for scroll position reading in refreshVirtualWindow
    this._virtualization.container = fauxScrollbar ?? this;

    // Cache whether any plugin has scroll handlers (checked once during setup)
    this.#hasScrollPlugins = this.#pluginManager?.getAll().some((p) => p.onScroll) ?? false;

    if (fauxScrollbar && rowsEl) {
      fauxScrollbar.addEventListener(
        'scroll',
        () => {
          // Fast exit if no scroll processing needed
          if (!this._virtualization.enabled && !this.#hasScrollPlugins) return;

          const rawScrollTop = fauxScrollbar.scrollTop;
          rowsEl.style.transform = `translateY(${computeRowsTranslateY(
            this._virtualization,
            this._rows.length,
            rawScrollTop,
          )}px)`;

          // Batch content update with requestAnimationFrame
          // Old content stays visible with smooth offset until new content renders
          this.#pendingScrollTop = rawScrollTop;
          if (!this.#scrollRaf) {
            this.#scrollRaf = requestAnimationFrame(() => {
              this.#scrollRaf = 0;
              if (this.#pendingScrollTop !== null) {
                this.#onScrollBatched(this.#pendingScrollTop);
                this.#pendingScrollTop = null;
              }
            });
          }
        },
        { passive: true, signal: scrollSignal },
      );

      // Horizontal scroll listener on scroll area
      // This is needed for plugins like column virtualization that need to respond to horizontal scroll
      const scrollArea = this.#renderRoot.querySelector('.tbw-scroll-area') as HTMLElement;
      this.#scrollAreaEl = scrollArea; // Store reference for use in #onScrollBatched
      this._virtualization.scrollAreaEl = scrollArea; // Cache for #calculateTotalSpacerHeight
      if (scrollArea && this.#hasScrollPlugins) {
        scrollArea.addEventListener(
          'scroll',
          () => {
            // Coalesce to one dispatch per frame. The browser can emit several
            // scroll events per frame (wheel/trackpad), and each dispatch both
            // forces layout (scrollHeight/scrollWidth/clientHeight/clientWidth)
            // and drives plugin work such as column virtualization re-render.
            // Reading inside the RAF also collapses those forced layouts to one.
            if (this.#hScrollRaf) return;
            this.#hScrollRaf = requestAnimationFrame(() => {
              this.#hScrollRaf = 0;
              const scrollEvent = this.#pooledScrollEvent;
              scrollEvent.scrollTop = fauxScrollbar.scrollTop;
              scrollEvent.scrollLeft = scrollArea.scrollLeft;
              scrollEvent.scrollHeight = fauxScrollbar.scrollHeight;
              scrollEvent.scrollWidth = scrollArea.scrollWidth;
              scrollEvent.clientHeight = fauxScrollbar.clientHeight;
              scrollEvent.clientWidth = scrollArea.clientWidth;
              this.#pluginManager?.onScroll(scrollEvent);
            });
          },
          { passive: true, signal: scrollSignal },
        );
      }

      // Forward wheel events from the content area to the faux scrollbar.
      // gridRoot may already BE .tbw-grid-content when the shell is active, so search from the render root.
      const gridContentEl = this.#renderRoot.querySelector('.tbw-grid-content') as HTMLElement;
      if (gridContentEl) {
        const scrollElements = { fauxScrollbar, scrollArea: this.#scrollAreaEl };
        setupWheelScrollListeners(gridContentEl, scrollElements, scrollSignal);
        setupTouchScrollListeners(gridContentEl, this.#touchState, scrollElements, scrollSignal);
      }
    }

    // Set up delegated event handlers for cell interactions (click, dblclick, keydown)
    // This replaces per-cell event listeners with a single set of delegated handlers
    // Dramatically reduces memory usage: 4 listeners total vs. 30,000+ for large grids
    if (this._bodyEl) {
      setupCellEventDelegation(this, this._bodyEl, scrollSignal);
    }

    // Disconnect existing resize observer before creating new one
    // This ensures we observe the NEW viewport element after DOM recreation
    this.#resizeObserver?.disconnect();

    // Resize observer to refresh virtualization when viewport size changes
    // (e.g., when footer is added, window resizes, or shell panel toggles)
    if (this._virtualization.viewportEl) {
      this.#resizeObserver = new ResizeObserver(() => {
        // Update cached geometry so refreshVirtualWindow can skip forced layout reads
        this.#updateCachedGeometry();
        // Use scheduler for viewport resize - batches with other pending work
        // The scheduler already batches multiple requests per RAF
        this.#scheduler.requestPhase(RenderPhase.VIRTUALIZATION, 'resize-observer');
      });
      this.#resizeObserver.observe(this._virtualization.viewportEl);
    }

    // Note: We no longer need to schedule init-virtualization here since
    // the initial FULL render from #setup already includes virtualization.
    // Requesting it again here caused duplicate renders on initialization.

    // Track focus state via data attribute
    setupFocusTracking(this, this.#renderRoot, this.#focusManager, scrollSignal);
  }

  // #endregion

  // #region Event System
  /**
   * Add a typed event listener for grid events.
   *
   * This override provides type-safe event handling for DataGrid-specific events.
   * The event detail is automatically typed based on the event name.
   *
   * **Prefer {@link on | grid.on()}** for most use cases — it auto-unwraps the
   * detail payload and returns an unsubscribe function for easy cleanup.
   *
   * Use `addEventListener` when you need standard DOM options like `once`,
   * `capture`, `passive`, or `signal` (AbortController).
   *
   * @example
   * ```typescript
   * // Recommended: use grid.on() instead
   * const off = grid.on('cell-click', (detail) => {
   *   console.log(detail.field, detail.value);
   * });
   *
   * // addEventListener is useful when you need DOM listener options
   * grid.addEventListener('cell-click', (e) => {
   *   console.log(e.detail.field, e.detail.value);
   * }, { once: true });
   *
   * // Or with AbortController for batch cleanup
   * const controller = new AbortController();
   * grid.addEventListener('sort-change', (e) => {
   *   fetchData({ sortBy: e.detail.field });
   * }, { signal: controller.signal });
   * controller.abort(); // removes all listeners tied to this signal
   * ```
   *
   * @category Events
   */
  override addEventListener<K extends keyof DataGridEventMap<T>>(
    type: K,
    listener: (this: DataGridElement<T>, ev: CustomEvent<DataGridEventMap<T>[K]>) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | ((ev: CustomEvent) => void),
    options?: boolean | AddEventListenerOptions,
  ): void {
    super.addEventListener(type, listener as EventListener, options);
  }

  /**
   * Remove a typed event listener for grid events.
   *
   * @category Events
   */
  override removeEventListener<K extends keyof DataGridEventMap<T>>(
    type: K,
    listener: (this: DataGridElement<T>, ev: CustomEvent<DataGridEventMap<T>[K]>) => void,
    options?: boolean | EventListenerOptions,
  ): void;
  override removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
  override removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | ((ev: CustomEvent) => void),
    options?: boolean | EventListenerOptions,
  ): void {
    super.removeEventListener(type, listener as EventListener, options);
  }

  /**
   * Subscribe to a typed grid event. **Recommended** over `addEventListener`.
   *
   * Returns an unsubscribe function — call it to remove the listener.
   * The listener receives the event **detail** as its first argument
   * (no need to dig into `e.detail`), and the raw `CustomEvent` as
   * the second argument when you need `preventDefault()` or `stopPropagation()`.
   *
   * @remarks
   * Advantages over `addEventListener`:
   * - Auto-unwraps `event.detail` — your callback receives the payload directly
   * - Returns an unsubscribe function — no need to keep a reference to the handler
   * - Fully typed — event name → detail type is inferred automatically
   *
   * Use `addEventListener` instead when you need DOM listener options
   * like `once`, `capture`, `passive`, or `signal` (AbortController).
   *
   * @example
   * ```typescript
   * // Basic usage — detail is auto-unwrapped
   * const off = grid.on('cell-click', ({ row, field, value }) => {
   *   console.log(`Clicked ${field} = ${value}`);
   * });
   *
   * // Clean up when done
   * off();
   * ```
   *
   * @example
   * ```typescript
   * // Access the raw event for preventDefault/stopPropagation
   * grid.on('cell-activate', (detail, event) => {
   *   if (detail.field === 'notes') {
   *     event.preventDefault(); // suppress default editing
   *     openRichTextEditor(detail.row, detail.cellEl);
   *   }
   * });
   * ```
   *
   * @example
   * ```typescript
   * // Multiple subscriptions with easy teardown
   * const unsubs = [
   *   grid.on('sort-change', ({ field, direction }) => {
   *     console.log(`Sorted by ${field} ${direction === 1 ? 'ASC' : 'DESC'}`);
   *   }),
   *   grid.on('column-resize', ({ field, width }) => {
   *     saveColumnWidth(field, width);
   *   }),
   *   grid.on('data-change', ({ rowCount }) => {
   *     statusBar.textContent = `${rowCount} rows`;
   *   }),
   * ];
   *
   * // Teardown all at once
   * unsubs.forEach((off) => off());
   * ```
   *
   * @category Events
   */
  on<K extends keyof DataGridEventMap<T>>(
    type: K,
    listener: (detail: DataGridEventMap<T>[K], event: CustomEvent<DataGridEventMap<T>[K]>) => void,
  ): () => void;
  on(type: string, listener: (detail: unknown, event: CustomEvent) => void): () => void;
  on(type: string, listener: (detail: unknown, event: CustomEvent) => void): () => void {
    const handler = ((e: CustomEvent) => {
      listener(e.detail, e);
    }) as EventListener;
    this.addEventListener(type, handler);
    return () => this.removeEventListener(type, handler);
  }

  #emit<D>(eventName: string, detail: D): void {
    if (!this.#connected) return;
    this.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
  }

  _emitDataChange(): void {
    this.#emit<DataChangeDetail>('data-change', {
      rowCount: this._rows.length,
      sourceRowCount: this.#rows.length,
    });
    // Announce "N rows loaded" only when the source row count actually changed.
    // Internal sort/filter/edit emits leave sourceRowCount unchanged, so this stays quiet
    // and doesn't conflict with sortApplied/filterApplied announcements.
    announceDataLoaded(this, this.#ariaState, this.#rows.length);
  }

  /** Update ARIA selection attributes on rendered rows/cells */
  #updateAriaSelection(): void {
    // Mark active row and cell with aria-selected
    const rows = this._bodyEl?.querySelectorAll('.data-grid-row');
    rows?.forEach((row, rowIdx) => {
      const isActiveRow = rowIdx === this._focusRow;
      row.setAttribute('aria-selected', String(isActiveRow));
      row.querySelectorAll('.cell').forEach((cell, colIdx) => {
        (cell as HTMLElement).setAttribute('aria-selected', String(isActiveRow && colIdx === this._focusCol));
      });
    });
  }
  // #endregion

  // #region Batched Update System
  // Allows multiple property changes within the same microtask to be coalesced
  // into a single update cycle, dramatically reducing redundant renders.

  /**
   * Queue an update for a specific property type.
   * All updates queued within the same microtask are batched together.
   */
  #queueUpdate(type: 'rows' | 'columns' | 'gridConfig' | 'fitMode' | 'columnInference'): void {
    this.#pendingUpdateFlags[type] = true;

    // If already queued, skip scheduling
    if (this.#pendingUpdate) return;

    this.#pendingUpdate = true;
    // Use queueMicrotask to batch synchronous property sets
    queueMicrotask(() => this.#flushPendingUpdates());
  }

  /**
   * Process all pending updates in optimal order.
   * Priority: gridConfig first (may affect all), then columns, rows, fitMode, editMode
   */
  #flushPendingUpdates(): void {
    if (!this.#pendingUpdate || !this.#connected) {
      this.#pendingUpdate = false;
      return;
    }

    const flags = this.#pendingUpdateFlags;

    // Reset flags before processing to allow new updates during processing
    this.#pendingUpdate = false;
    this.#pendingUpdateFlags = {
      rows: false,
      columns: false,
      gridConfig: false,
      fitMode: false,
      columnInference: false,
    };

    // If gridConfig changed, it supersedes columns/fit changes
    // but we still need to handle rows if set separately
    if (flags.gridConfig) {
      this.#applyGridConfigUpdate();
      // Still process rows if set separately (e.g., grid.gridConfig = ...; grid.rows = ...;)
      if (flags.rows) {
        this.#applyRowsUpdate();
      }
      return;
    }

    // Process remaining changes in dependency order
    if (flags.columns || flags.columnInference) {
      this.#applyColumnsUpdate();
    }
    if (flags.rows) {
      this.#applyRowsUpdate();
    }
    if (flags.fitMode) {
      this.#applyFitModeUpdate();
    }
  }

  // Individual update applicators - these do the actual work
  #applyRowsUpdate(): void {
    this._rows = Array.isArray(this.#rows) ? [...this.#rows] : [];
    // Mark row ID map dirty — defer the O(n) rebuild until a consumer actually
    // needs it (getRow / _getRowEntry via #ensureRowIdMap). #rebuildRowModel
    // will rebuild it unconditionally during the scheduler flush, so eager
    // rebuild here only wastes cycles when no one calls getRow() between the
    // setter and the RAF. The lazy pattern preserves the same invariant as the
    // previous eager _rebuildRowIdMap() call: the map is always current at read time.
    this.#rowIdMapDirty = true;
    // Request a ROWS phase render through the scheduler.
    // This batches with any other pending work (e.g., React adapter's refreshColumns).
    this.#scheduler.requestPhase(RenderPhase.ROWS, 'applyRowsUpdate');
  }

  /**
   * Ensure the row ID map is current. Call before any map read.
   */
  #ensureRowIdMap(): void {
    if (this.#rowIdMapDirty) {
      this._rebuildRowIdMap();
    }
  }

  /**
   * Rebuild the row ID map for O(1) lookups.
   * Called when rows array changes.
   */
  _rebuildRowIdMap(): void {
    this.#rowIdMapDirty = false;
    this.#rowIdMap.clear();
    const getRowId = this.#effectiveConfig.getRowId;

    for (let index = 0; index < this._rows.length; index++) {
      const id = tryResolveRowId(this._rows[index], getRowId);
      if (id !== undefined) {
        this.#rowIdMap.set(id, { row: this._rows[index], index });
      }
      // Rows without IDs are skipped - they won't be accessible via getRow/updateRow
    }
  }

  #applyColumnsUpdate(): void {
    invalidateCellCache(this);
    this.#configManager.merge();
    this.#setup();
  }

  #applyFitModeUpdate(): void {
    this.#configManager.merge();
    const mode = this.#effectiveConfig.fitMode;
    if (mode === 'fixed') {
      this.__didInitialAutoSize = false;
      autoSizeColumns(this);
    } else {
      for (let i = 0; i < this._columns.length; i++) {
        const c = this._columns[i] as any;
        if (!c.__userResized && c.__autoSized) delete c.width;
      }
      updateTemplate(this);
    }
  }

  #applyGridConfigUpdate(): void {
    // Shell light-DOM (header / tool-buttons / tool-panels) is parsed by the
    // ShellPlugin's `processConfig` + `parseLightDom` hooks on each merge below —
    // core holds no shell parse knowledge (extraction #370).
    this.#configManager.parseLightDomColumns(this);
    this.#configManager.merge();
    this.#updatePluginConfigs();

    // Re-check variable heights after config merge: rowHeight may have changed
    // from a number to a function (or vice versa) without any plugin changes.
    this.#virtManager.configureVariableHeights();

    // Second merge so plugin hooks re-fold any contributions parsed/collected
    // after plugin (re-)initialization.
    this.#configManager.markSourcesChanged();
    this.#configManager.merge();

    // Re-apply `gridConfig.columnState` (if any) after the source rebuild.
    // The merges above clone `effectiveConfig.columns` fresh from the original
    // gridConfig.columns, wiping any runtime visibility/width/order mutations
    // (col.hidden written by setColumnVisible / applyColumnState). If the new
    // gridConfig carries a `columnState`, `#collectAllSources` re-stored it
    // into `#initialColumnState`; consume it here so the declared state is
    // honoured on every gridConfig (re-)assignment, not just initial setup.
    // Regression: liquids-operations-roma cargo-list — saved preset's hidden
    // columns became visible after several filter/refetch cycles caused the
    // memoized gridConfig ref to rebuild.
    const pendingInitialState = this.#configManager.initialColumnState;
    if (pendingInitialState) {
      this.#configManager.initialColumnState = undefined;
      this.#initialColumnState = undefined;
      const plugins = (this.#pluginManager?.getAll() ?? []) as BaseGridPlugin[];
      this.#configManager.applyState(pendingInitialState, plugins);
    }

    // Ask plugins whether the structural DOM signature changed (shell header
    // appearing/disappearing, tool-panel count/position/mode). Generic hook —
    // core holds no shell-specific re-render logic (extraction #370). The hook
    // reads the still-current (old) DOM vs the freshly merged config.
    const needsFullRerender = this.#pluginManager?.needsStructuralRerender() ?? false;

    if (needsFullRerender) {
      // Plugins move relocated light-DOM nodes back ahead of the rebuild.
      this.#pluginManager?.beforeStructuralRender();
      this.#render();
      this.#injectAllPluginStyles();
      this.#afterConnect();
      this._rebuildRowIdMap();
      return;
    }

    // In-place shell header sync + open tool-panel re-render now happen in the
    // ShellPlugin's `afterRender` hook, fired by the COLUMNS render below.
    this._rebuildRowIdMap();
    this.#scheduler.requestPhase(RenderPhase.COLUMNS, 'applyGridConfigUpdate');
  }

  // NOTE: Legacy watch handlers have been replaced by the batched update system.
  // The #queueUpdate() method schedules updates which are processed by #flushPendingUpdates()
  // and individual #apply*Update() methods. This coalesces rapid property changes
  // (e.g., setting rows, columns, gridConfig in quick succession) into a single update cycle.

  #processColumns(): void {
    // Bump the row-render epoch so that renderVisibleRows knows the column
    // structure may have changed and triggers a full inline rebuild for each
    // pooled row element.  This is intentionally done here — NOT inside
    // refreshVirtualWindow — so that a pure ROWS-phase refresh (data change,
    // same columns) can skip the expensive destroy-and-recreate cycle and
    // use fastPatchRow instead.
    this.__rowRenderEpoch++;

    // Let plugins process visible columns (column grouping, etc.)
    // Start from base columns (before any plugin transformation) - like #rebuildRowModel uses #rows
    if (this.#pluginManager) {
      // Use base columns as source of truth, falling back to current _columns if not set.
      // Plugins that MOVE columns do so here on the full list (hidden included), so a hidden
      // column already sits where it will appear once shown (#512).
      const sourceColumns = this.#pluginManager.orderColumns(
        this.#baseColumns.length > 0 ? this.#baseColumns : this._columns,
      );
      const visibleCols = sourceColumns.filter((c) => !c.hidden);
      const hiddenCols = sourceColumns.filter((c) => c.hidden);
      const processedColumns = this.#pluginManager.processColumns([...visibleCols]);

      // If plugins modified visible columns, update them
      if (processedColumns !== visibleCols) {
        // Build set for quick lookup
        const processedFields = new Set(processedColumns.map((c: ColumnInternal) => c.field));

        // Check if this is a complete column replacement (e.g., pivot mode)
        // If no processed columns match original columns, use processed columns directly
        const hasMatchingFields = visibleCols.some((c) => processedFields.has(c.field));

        if (!hasMatchingFields && processedColumns.length > 0) {
          // Complete replacement: use processed columns directly (pivot mode)
          // Preserve hidden columns at the end
          this._columns = [...processedColumns, ...hiddenCols] as ColumnInternal<T>[];
        } else if (hiddenCols.length === 0) {
          this._columns = processedColumns as ColumnInternal<T>[];
        } else {
          // Plugins may have modified, added, or dropped visible columns — their order is
          // authoritative. Hidden columns are interleaved next to their unmoved source
          // neighbours so showing one later restores it where it belongs (#512).
          this._columns = interleaveHiddenColumns(sourceColumns, processedColumns as ColumnInternal<T>[]);
        }
      } else {
        // Plugins returned columns unchanged, but we may need to restore from base
        this._columns = [...sourceColumns] as ColumnInternal<T>[];
      }
    }
  }

  /** Recompute row model via plugin hooks. */
  #rebuildRowModel(): void {
    // Invalidate cell display value cache - rows are changing
    invalidateCellCache(this);

    const baseRows = this.#rows;
    if (!Array.isArray(baseRows)) {
      this._rows = [];
      this._rebuildRowIdMap();
      if (this._virtualization.variableHeights) {
        this.#virtManager.initializePositionCache();
      }
      this._emitDataChange();
      return;
    }

    // Apply initialSort on first data load (no active sort and config specifies one)
    if (!this._sortState && this.#effectiveConfig.initialSort) {
      const { field, direction } = this.#effectiveConfig.initialSort;
      const col = (this._columns as ColumnConfig<T>[]).find((c) => c.field === field);
      if (col) {
        this._sortState = { field, direction: direction === 'desc' ? -1 : 1 };
      }
    }

    // When sort is active, reuse the existing _rows copy (from #applyRowsUpdate) as
    // __originalOrder instead of creating another O(n) slice inside reapplyCoreSort.
    // Then make a single fresh copy for in-place sorting.
    if (this._sortState) {
      this.__originalOrder = this._rows; // Already a safe copy
    }
    const originalRows = this._sortState ? [...baseRows] : baseRows;

    // Re-apply core sort before plugins so sorted order is maintained on data refresh.
    // This runs BEFORE processRows so grouping/filtering work on sorted data.
    // skipOriginalOrderSave=true: we already saved __originalOrder above.
    const sortedRows = reapplyCoreSort(this, originalRows, true);

    // Let plugins process rows (they may add, remove, transform rows)
    // Plugins can add markers for specialized rendering via the renderRow hook
    const processedRows = this.#pluginManager?.processRows(sortedRows) ?? sortedRows;

    // Store processed rows for rendering
    // Note: processedRows may contain group markers that plugins handle via renderRow hook
    this._rows = processedRows as T[];

    // Mark row ID map dirty rather than rebuilding eagerly. The map is O(n) to build
    // (≈130ms at 1M rows) and is only consumed by getRow / _getRowEntry / plugin
    // afterRender hooks via #ensureRowIdMap. Most renders never read it, so the lazy
    // rebuild eliminates the cost from the initial-render hot path while preserving
    // the invariant that the map is current at read time.
    this.#rowIdMapDirty = true;

    // Position cache rebuild is NOT needed here — the scheduler always calls
    // refreshVirtualWindow(force=true) after _schedulerProcessRows(), which
    // rebuilds the position cache. Doing it here would double the O(n) work.

    this._emitDataChange();
  }

  // `_applyAnimationConfig` (called by ConfigManager after merge) lives in the
  // "Methods exposed for extracted managers" region below \u2014 there is no
  // private wrapper because there are no in-class callers besides that path.
  // #endregion

  // #region Internal Helpers
  _renderVisibleRows(start: number, end: number, epoch = this.__rowRenderEpoch): void {
    // Use cached hook to avoid creating closures on every render (hot path optimization)
    if (!this.#renderRowHook) {
      this.#renderRowHook = (row: any, rowEl: HTMLElement, rowIndex: number): boolean => {
        return this.#pluginManager?.renderRow(row, rowEl, rowIndex) ?? false;
      };
    }
    renderVisibleRows(this, start, end, epoch, this.#renderRowHook);

    // Re-apply active loading states after row rendering rebuilds pooled DOM.
    if (this.#loadingRows.size > 0) {
      for (const rowId of this.#loadingRows) {
        this.#updateRowLoadingState(rowId, true);
      }
    }

    if (this.#loadingCells.size > 0) {
      for (const [rowId, fields] of this.#loadingCells) {
        for (const field of fields) {
          this.#updateCellLoadingState(rowId, field, true);
        }
      }
    }
  }

  // ARIA state - uses external module for pure functions
  #ariaState: AriaState = createAriaState();

  /** Updates ARIA row/col counts. Delegates to aria.ts module. */
  _updateAriaCounts(rowCount: number, colCount: number): void {
    updateAriaCounts(this.#ariaState, this.__rowsBodyEl, this._bodyEl, rowCount, colCount);
  }

  // --- Methods exposed for extracted managers ---

  /** @internal Request a render at the given phase through the scheduler. */
  _requestSchedulerPhase(phase: RenderPhase, source: string): void {
    this.#scheduler.requestPhase(phase, source);
  }

  /** @internal Plugin-specific row height override. */
  _getPluginRowHeight(row: T, index: number): number | undefined {
    return this.#pluginManager?.getRowHeight?.(row, index);
  }

  /** @internal Let plugins adjust the virtual start index backwards. */
  _adjustPluginVirtualStart(start: number, scrollTop: number, rowHeight: number): number | undefined {
    return this.#pluginManager?.adjustVirtualStart(start, scrollTop, rowHeight);
  }

  /** @internal Run plugin afterRender hooks. */
  _afterPluginRender(): void {
    this.#pluginManager?.afterRender();
  }

  /** @internal Emit a plugin event through the plugin manager. */
  _emitPluginEvent(event: string, detail: unknown): void {
    this.#pluginManager?.emitPluginEvent(event, detail);
  }

  // --- Scheduler pipeline methods ---

  /** @internal Merge effective config (FULL/COLUMNS phase). */
  _schedulerMergeConfig(): void {
    this.#configManager.parseLightDomColumns(this);
    this.#configManager.merge();
    this.#updatePluginConfigs();
    validatePluginProperties(this.#effectiveConfig, this.#pluginManager?.getPlugins() ?? [], this.id);
    validatePluginConfigRules(this.#pluginManager?.getPlugins() ?? [], this.id);
    validatePluginIncompatibilities(this.#pluginManager?.getPlugins() ?? [], this.id);
    this.#updateAriaLabels();
    this.#baseColumns = [...this._columns];
  }

  /** @internal Process columns through plugins (COLUMNS phase). */
  _schedulerProcessColumns(): void {
    this.#processColumns();
  }

  /** @internal Rebuild row model through plugins (ROWS phase). */
  _schedulerProcessRows(): void {
    this.#rebuildRowModel();
  }

  /** @internal Render header DOM (HEADER phase). */
  _schedulerRenderHeader(): void {
    renderHeader(this);
  }

  /** @internal Update CSS grid template (COLUMNS phase). */
  _schedulerUpdateTemplate(): void {
    updateTemplate(this);
  }

  /** @internal Run afterRender hooks and post-render bookkeeping (STYLE phase). */
  _schedulerAfterRender(): void {
    this.#pluginManager?.afterRender();

    // Recompute the empty-state overlay AFTER plugin afterRender so plugins
    // that mutate `_rows` or row visibility (server-side, tree, grouping,
    // filtering) are reflected in the visible-or-not decision.
    this.#updateEmptyOverlay();

    // Recalculate spacer height after plugins modify the DOM in afterRender.
    if (this._virtualization.enabled && this._virtualization.totalHeightEl) {
      queueMicrotask(() => {
        if (!this._virtualization.totalHeightEl) return;
        const newTotalHeight = this.#virtManager.calculateTotalSpacerHeight(this._rows.length);
        this._virtualization.totalHeightEl.style.height = `${newTotalHeight}px`;
      });
    }

    // Auto-size columns on first render if fitMode is 'fixed'
    const mode = this.#effectiveConfig.fitMode;
    if (mode === 'fixed' && !this.__didInitialAutoSize) {
      this.__didInitialAutoSize = true;
      autoSizeColumns(this);
    }
    // Restore focus styling if requested by a plugin
    if (this._restoreFocusAfterRender) {
      this._restoreFocusAfterRender = false;
      ensureCellVisible(this);
    }
    // Install the first-row height observer and run the deferred base-height measurement
    // now that rows exist in the DOM.
    this.#virtManager.afterRenderRowHeights();

    // Show loading overlay if loading was set before the grid root was created.
    if (this.#loading) {
      this.#updateLoadingOverlay();
    }
  }

  /** @internal Whether the grid is fully connected (scheduler guard). */
  get _schedulerIsConnected(): boolean {
    return this.isConnected && this.#connected;
  }

  // Shell controller & config manager support (replaces callback closures)
  /** @internal The grid's host HTMLElement. Use instead of casting `grid as unknown as HTMLElement`. */
  get _hostElement(): HTMLElement {
    return this;
  }

  /** @internal The grid's render root element for DOM queries. */
  get _renderRoot(): HTMLElement {
    return this.#renderRoot;
  }

  /** @internal Emit a custom event from the grid. */
  _emit(eventName: string, detail: unknown): void {
    this.#emit(eventName, detail);
  }

  /** @internal Get accordion expand/collapse icons from effective config. */
  get _accordionIcons(): { expand: IconValue; collapse: IconValue } {
    return {
      expand: this.#effectiveConfig?.icons?.expand ?? DEFAULT_GRID_ICONS.expand,
      collapse: this.#effectiveConfig?.icons?.collapse ?? DEFAULT_GRID_ICONS.collapse,
    };
  }

  /**
   * @internal Tool-panel + header-content contributions gathered from all
   * attached plugins. The ShellPlugin polls this from its own `processConfig`
   * hook to self-collect plugin shell contributions (extraction #370).
   */
  _pluginShellContributions(): { toolPanels: ToolPanelDefinition[]; headerContents: HeaderContentDefinition[] } {
    const toolPanels = (this.#pluginManager?.getToolPanels() ?? []).map((entry) => entry.panel);
    const headerContents = (this.#pluginManager?.getHeaderContents() ?? []).map((entry) => entry.content);
    return { toolPanels, headerContents };
  }

  /** @internal Clear the row pool and body element. */
  _clearRowPool(): void {
    // Release adapter-managed cells BEFORE wiping the body. Without this,
    // `innerHTML = ''` detaches portals/teleports without notifying the
    // framework adapter, which then crashes on its next commit when it
    // tries to remove now-orphan children (#250).
    //
    // Wrap the per-cell release loop in adapter.beginBatch / endBatch so
    // adapters that synchronously commit teardown per cell (React's
    // `flushSync`) defer those commits to a single deferred render once
    // `bodyEl.innerHTML = ''` has detached every cell. Eliminates the
    // per-cell `flushSync was called from inside a lifecycle method`
    // warning storm on grouping changes (#330).
    const adapter = this.__frameworkAdapter;
    if (adapter?.releaseCell || adapter?.unmount) {
      adapter.beginBatch?.(this);
      try {
        for (let r = 0; r < this._rowPool.length; r++) {
          releasePooledRow(adapter, this._rowPool[r]);
        }
        this._rowPool.length = 0;
        if (this._bodyEl) this._bodyEl.innerHTML = '';
      } finally {
        adapter.endBatch?.(this);
      }
    } else {
      this._rowPool.length = 0;
      if (this._bodyEl) this._bodyEl.innerHTML = '';
    }
    this.__rowRenderEpoch++;
  }

  /** @internal Run grid setup (DOM rebuild). */
  _setup(): void {
    this.#setup();
  }

  /** @internal Apply animation configuration to host element. */
  _applyAnimationConfig(gridConfig: GridConfig<T>): void {
    const config: AnimationConfig = {
      ...DEFAULT_ANIMATION_CONFIG,
      ...gridConfig.animation,
    };

    // Resolve animation mode
    const mode = config.mode ?? 'reduced-motion';
    let enabled: 0 | 1 = 1;

    if (mode === false || mode === 'off') {
      enabled = 0;
    } else if (mode === true || mode === 'on') {
      enabled = 1;
    }
    // 'reduced-motion' stays 1 here and is flipped to 0 by the media query in
    // media-queries.css, which needs `!important` to outrank these inline writes.

    // Set CSS custom properties
    this.style.setProperty('--tbw-animation-duration', `${config.duration}ms`);
    this.style.setProperty('--tbw-animation-easing', config.easing ?? 'ease-out');
    this.style.setProperty('--tbw-animation-enabled', String(enabled));

    // Set data attribute for mode-based CSS selectors
    this.dataset.animationMode = typeof mode === 'boolean' ? (mode ? 'on' : 'off') : mode;
  }

  /** Updates ARIA label and describedby attributes. Delegates to aria.ts module. */
  #updateAriaLabels(): void {
    updateAriaLabels(this.#ariaState, this.__rowsBodyEl, this.#effectiveConfig);
  }

  /**
   * Update the loading overlay visibility.
   * Called when `loading` property changes.
   */
  #updateLoadingOverlay(): void {
    const gridRoot = this.querySelector('.tbw-grid-root');
    if (!gridRoot) return;

    if (this.#loading) {
      // Create overlay if it doesn't exist
      if (!this.#loadingOverlayEl) {
        this.#loadingOverlayEl = createLoadingOverlay(this.#effectiveConfig?.loadingRenderer);
      }
      showLoadingOverlay(gridRoot, this.#loadingOverlayEl);
    } else {
      hideLoadingOverlay(this.#loadingOverlayEl);
    }
  }

  /**
   * Update the empty-state overlay visibility.
   *
   * Called from `_schedulerAfterRender` (every render cycle, so the overlay
   * tracks plugin-driven row count changes — filtering, server-side, tree
   * collapse) and from the `loading` setter (for prompt show/hide on the
   * loading transition without waiting for the next RAF).
   */
  #updateEmptyOverlay(): void {
    updateEmptyOverlay(
      this.querySelector('.tbw-grid-root'),
      this.#loading,
      this._rows.length,
      this.#rows,
      this.#effectiveConfig?.emptyRenderer,
      this.#effectiveConfig?.emptyOverlay ?? 'rows',
      this.#emptyOverlay,
      this,
    );
  }

  /**
   * Update a row's loading state in the DOM.
   * @param rowId - The row's unique identifier
   * @param loading - Whether the row is loading
   */
  #updateRowLoadingState(rowId: string, loading: boolean): void {
    // Find the row element by row ID
    this.#ensureRowIdMap();
    const rowData = this.#rowIdMap.get(rowId);
    if (!rowData) return;

    const rowEl = this.findRenderedRowElement?.(rowData.index);
    if (!rowEl) return;

    setRowLoadingState(rowEl, loading, this.#effectiveConfig?.loadingRenderer, this.__frameworkAdapter);
  }

  /**
   * Update a cell's loading state in the DOM.
   * @param rowId - The row's unique identifier
   * @param field - The column field
   * @param loading - Whether the cell is loading
   */
  #updateCellLoadingState(rowId: string, field: string, loading: boolean): void {
    // Find the row element by row ID
    this.#ensureRowIdMap();
    const rowData = this.#rowIdMap.get(rowId);
    if (!rowData) return;

    const rowEl = this.findRenderedRowElement?.(rowData.index);
    if (!rowEl) return;

    // Find the cell by field
    const colIndex = this._visibleColumns.findIndex((c) => c.field === field);
    if (colIndex < 0) return;

    // Match the `data-col` marker rather than indexing `children`: a plugin-rendered
    // custom row (responsive card, group header) and the row-loading overlay also live
    // there, so a positional lookup can land on non-cell DOM.
    const cellEl = rowEl.querySelector<HTMLElement>(`:scope > .cell[data-col="${colIndex}"]`);
    if (!cellEl) return;

    setCellLoadingState(cellEl, loading, this.#effectiveConfig?.loadingRenderer, this.__frameworkAdapter);
  }

  /**
   * Request a full grid re-setup through the render scheduler.
   * This method queues all the config merging, column/row processing, and rendering
   * to happen in the next animation frame via the scheduler.
   *
   * Previously this method executed rendering synchronously, but that caused race
   * conditions with framework adapters that also schedule their own render work.
   */
  #setup(): void {
    if (!this.isConnected) return;
    if (!this._headerRowEl || !this._bodyEl) {
      return;
    }

    // Read light DOM column configuration (synchronous DOM read)
    this.#configManager.parseLightDomColumns(this);

    // Apply initial column state synchronously if present
    // (needs to happen before scheduler to avoid flash of unstyled content)
    if (this.#initialColumnState) {
      const state = this.#initialColumnState;
      this.#initialColumnState = undefined; // Clear to avoid re-applying
      // Temporarily merge config so applyState has columns to work with
      this.#configManager.merge();
      const plugins = (this.#pluginManager?.getAll() ?? []) as BaseGridPlugin[];
      this.#configManager.applyState(state, plugins);
    }

    // Ensure legacy inline grid styles are cleared from container
    if (this._bodyEl) {
      this._bodyEl.style.display = '';
      this._bodyEl.style.gridTemplateColumns = '';
    }

    // Request full render through scheduler - batches with framework adapter work
    this.#scheduler.requestPhase(RenderPhase.FULL, 'setup');
  }

  #onScrollBatched(scrollTop: number): void {
    // PERF: Read all geometry values BEFORE DOM writes to avoid forced synchronous layout.
    // refreshVirtualWindow and onScrollRender write to the DOM (transforms, innerHTML, attributes).
    // Reading geometry after those writes forces the browser to synchronously compute layout.
    // By reading first, we batch reads together, then do all writes.
    //
    // Reads are unconditional now because the public `tbw-scroll` event always fires
    // (and needs scrollHeight/clientHeight for its detail). The plugin pooled-event
    // path also consumes these values when #hasScrollPlugins is true.
    const fauxScrollbar = this._virtualization.container;
    const scrollArea = this.#scrollAreaEl;
    const scrollLeft = scrollArea?.scrollLeft ?? 0;
    const scrollHeight = fauxScrollbar?.scrollHeight ?? 0;
    const scrollWidth = scrollArea?.scrollWidth ?? 0;
    const clientHeight = fauxScrollbar?.clientHeight ?? 0;
    const clientWidth = scrollArea?.clientWidth ?? 0;

    // Faux scrollbar pattern: content never scrolls, just update transforms
    // Old content stays visible until new transforms are applied
    const windowChanged = this.refreshVirtualWindow(false);

    // Let plugins reapply visual state to recycled DOM elements
    // Only run when the visible window actually changed to avoid expensive DOM queries
    if (windowChanged) {
      this.#pluginManager?.onScrollRender();
    }

    // Schedule debounced measurement for variable heights mode
    // This progressively builds up the height cache as user scrolls
    if (this._virtualization.variableHeights) {
      if (this.#scrollMeasureTimeout) {
        clearTimeout(this.#scrollMeasureTimeout);
      }
      // Measure after scroll settles (100ms debounce)
      this.#scrollMeasureTimeout = window.setTimeout(() => {
        this.#scrollMeasureTimeout = 0;
        this.#virtManager.measureRenderedRowHeights(this._virtualization.start, this._virtualization.end);
      }, 100);
    }

    // Dispatch to plugins (using cached flag and pooled event object to avoid GC)
    // Geometry values were read before DOM writes above - use pre-read values.
    if (this.#hasScrollPlugins) {
      const scrollEvent = this.#pooledScrollEvent;
      scrollEvent.scrollTop = scrollTop;
      scrollEvent.scrollLeft = scrollLeft;
      scrollEvent.scrollHeight = scrollHeight;
      scrollEvent.scrollWidth = scrollWidth;
      scrollEvent.clientHeight = clientHeight;
      scrollEvent.clientWidth = clientWidth;
      this.#pluginManager?.onScroll(scrollEvent);
    }

    // Public `tbw-scroll` CustomEvent — always dispatched (vertical only).
    // Detail must be a fresh object literal: consumers may retain or store it,
    // unlike the pooled #pooledScrollEvent which is mutated in place each tick.
    this.#emit<TbwScrollDetail>('tbw-scroll', {
      scrollTop,
      scrollHeight,
      clientHeight,
      direction: 'vertical',
    });
  }

  /**
   * Find the header row element.
   * Used by plugins that need to access header cells for styling or measurement.
   * @group DOM Access
   * @internal Plugin API
   */
  findHeaderRow(): HTMLElement {
    return this.#renderRoot.querySelector('.header-row') as HTMLElement;
  }

  /**
   * Find a rendered row element by its data row index.
   * Returns null if the row is not currently rendered (virtualized out of view).
   * Used by plugins that need to access specific row elements for styling or measurement.
   * @group DOM Access
   * @internal Plugin API
   * @param rowIndex - The data row index (not the DOM position)
   */
  findRenderedRowElement(rowIndex: number): HTMLElement | null {
    const s = this._virtualization;
    const poolIndex = rowIndex - s.start;
    if (poolIndex >= 0 && poolIndex < this._rowPool.length && poolIndex < s.end - s.start) {
      return this._rowPool[poolIndex];
    }
    return null;
  }

  /**
   * Dispatch a cell click event to the plugin system, then emit a public event.
   * Plugins get first chance to handle the event. After plugins process it,
   * a `cell-click` CustomEvent is dispatched for external listeners.
   *
   * @returns `true` if any plugin handled (consumed) the event, or if consumer canceled
   * @fires cell-activate - Unified activation event (cancelable) - fires FIRST
   * @fires cell-click - Emitted after plugins process the click, with full cell context
   */
  _dispatchCellClick(event: MouseEvent, rowIndex: number, colIndex: number, cellEl: HTMLElement): boolean {
    const row = this._rows[rowIndex];
    // colIndex from data-col is a visible-column index (rendering uses _visibleColumns).
    // Use _visibleColumns so the resolved column matches the clicked cell.
    const col = this._visibleColumns[colIndex];
    if (!row || !col) return false;

    const field = col.field;
    const value = readCellField(row, field);

    // Emit cell-activate FIRST (cancelable) - consumers can prevent plugin behavior
    const activateEvent = new CustomEvent('cell-activate', {
      cancelable: true,
      bubbles: true,
      composed: true,
      detail: {
        rowIndex,
        colIndex,
        column: col,
        field,
        value,
        row,
        cellEl,
        trigger: 'pointer' as const,
        originalEvent: event,
      },
    });
    this.dispatchEvent(activateEvent);

    // If consumer canceled, don't let plugins handle it
    if (activateEvent.defaultPrevented) {
      return true; // Treated as "handled"
    }

    const cellClickEvent: CellClickEvent = {
      row,
      rowIndex,
      colIndex,
      column: col,
      field,
      value,
      cellEl,
      originalEvent: event,
    };

    // Let plugins handle (editing, selection, etc.)
    const handled = this.#pluginManager?.onCellClick(cellClickEvent) ?? false;

    // Emit informational cell-click event for external listeners
    this.#emit('cell-click', cellClickEvent);

    return handled;
  }

  /**
   * Dispatch a row click event to the plugin system, then emit a public event.
   * Plugins get first chance to handle the event. After plugins process it,
   * a `row-click` CustomEvent is dispatched for external listeners.
   *
   * @returns `true` if any plugin handled (consumed) the event
   * @fires row-click - Emitted after plugins process the click, with full row context
   */
  _dispatchRowClick(event: MouseEvent, rowIndex: number, row: any, rowEl: HTMLElement): boolean {
    if (!row) return false;

    const rowClickEvent: RowClickEvent = {
      rowIndex,
      row,
      rowEl,
      originalEvent: event,
    };

    // Let plugins handle first
    const handled = this.#pluginManager?.onRowClick(rowClickEvent) ?? false;

    // Emit public event for external listeners (reuse same event object)
    this.#emit('row-click', rowClickEvent);

    return handled;
  }

  /**
   * Dispatch a header click event to the plugin system.
   * Returns true if any plugin handled the event.
   */
  _dispatchHeaderClick(event: MouseEvent | KeyboardEvent, col: ColumnConfig, headerEl: HTMLElement): boolean {
    if (!col) return false;

    const headerClickEvent: HeaderClickEvent = {
      colIndex: this._columns.indexOf(col as ColumnInternal<T>),
      field: col.field,
      column: col,
      headerEl,
      originalEvent: event,
    };

    return this.#pluginManager?.onHeaderClick(headerClickEvent) ?? false;
  }

  /**
   * Dispatch a keyboard event to the plugin system.
   * Returns true if any plugin handled the event.
   */
  _dispatchKeyDown(event: KeyboardEvent): boolean {
    return this.#pluginManager?.onKeyDown(event) ?? false;
  }

  /**
   * Get horizontal scroll boundary offsets from plugins.
   * Used by keyboard navigation to ensure focused cells are fully visible
   * when plugins like pinned columns obscure part of the scroll area.
   */
  _getHorizontalScrollOffsets(
    rowEl?: HTMLElement,
    focusedCell?: HTMLElement,
  ): { left: number; right: number; skipScroll?: boolean } {
    return this.#pluginManager?.getHorizontalScrollOffsets(rowEl, focusedCell) ?? { left: 0, right: 0 };
  }

  /**
   * Get vertical scroll boundary offsets from plugins.
   * Used by keyboard navigation so a focused row is not left hidden beneath an
   * overlay such as the sticky-rows clones (WCAG 2.2 SC 2.4.11).
   */
  _getVerticalScrollOffsets(focusedRowIndex?: number): { top: number; bottom: number; skipScroll?: boolean } {
    return this.#pluginManager?.getVerticalScrollOffsets(focusedRowIndex) ?? { top: 0, bottom: 0 };
  }

  /**
   * Query plugins with a simplified API.
   * This is a convenience wrapper around `queryPlugins` that uses a flat signature.
   *
   * @param type - The query type (e.g., 'canMoveColumn')
   * @param context - The query context/parameters
   * @returns Array of non-undefined responses from plugins
   * @group Plugin Communication
   * @internal Plugin API
   *
   * @example
   * // Check if any plugin vetoes moving a column
   * const responses = grid.query<boolean>('canMoveColumn', column);
   * const canMove = !responses.includes(false);
   *
   * // Get context menu items from all plugins
   * const items = grid.query<ContextMenuItem[]>('getContextMenuItems', params).flat();
   */
  query<T>(type: string, context: unknown): T[] {
    return this.#pluginManager?.queryPlugins<T>({ type, context }) ?? [];
  }

  /**
   * Dispatch cell mouse events for drag operations.
   * Returns true if any plugin started a drag.
   * @group Event Dispatching
   * @internal Plugin API - called by event-delegation.ts
   */
  _dispatchCellMouseDown(event: CellMouseEvent): boolean {
    return this.#pluginManager?.onCellMouseDown(event) ?? false;
  }

  /**
   * Dispatch cell mouse move during drag.
   * @group Event Dispatching
   * @internal Plugin API - called by event-delegation.ts
   */
  _dispatchCellMouseMove(event: CellMouseEvent): void {
    this.#pluginManager?.onCellMouseMove(event);
  }

  /**
   * Dispatch cell mouse up to end drag.
   * @group Event Dispatching
   * @internal Plugin API - called by event-delegation.ts
   */
  _dispatchCellMouseUp(event: CellMouseEvent): void {
    this.#pluginManager?.onCellMouseUp(event);
  }

  /**
   * Call afterCellRender hook on all plugins.
   * This is called by rows.ts for each cell after it's rendered,
   * allowing plugins to modify cells during render rather than
   * requiring expensive post-render DOM queries.
   *
   * @group Plugin Hooks
   * @internal Plugin API - called by rows.ts
   */
  _afterCellRender(context: AfterCellRenderContext<T>): void {
    // Cast needed because PluginManager uses unknown for row type
    this.#pluginManager?.afterCellRender(context as AfterCellRenderContext);
  }

  /**
   * Check if any plugin has registered an afterCellRender hook.
   * Used to skip the hook call entirely for performance when no plugins need it.
   *
   * @group Plugin Hooks
   * @internal Plugin API - called by rows.ts
   */
  _hasAfterCellRenderHook(): boolean {
    return this.#pluginManager?.hasAfterCellRenderHook() ?? false;
  }

  /**
   * Call afterRowRender hook on all plugins that have registered for it.
   * Called by rows.ts after each row is completely rendered.
   *
   * @param context - Context containing row data, index, and DOM element
   *
   * @group Plugin Hooks
   * @internal Plugin API - called by rows.ts
   */
  _afterRowRender(context: AfterRowRenderContext<T>): void {
    // Cast needed because PluginManager uses unknown for row type
    this.#pluginManager?.afterRowRender(context as AfterRowRenderContext);
  }

  /**
   * Check if any plugin has registered an afterRowRender hook.
   * Used to skip the hook call entirely for performance when no plugins need it.
   *
   * @group Plugin Hooks
   * @internal Plugin API - called by rows.ts
   */
  _hasAfterRowRenderHook(): boolean {
    return this.#pluginManager?.hasAfterRowRenderHook() ?? false;
  }

  /**
   * Wait for the grid to be ready.
   * Resolves once the component has finished initial setup, including
   * column inference, plugin initialization, and first render.
   *
   * @group Lifecycle
   * @returns Promise that resolves when the grid is ready
   *
   * @example
   * ```typescript
   * const grid = queryGrid('tbw-grid');
   * await grid.ready();
   * console.log('Grid is ready, rows:', grid.rows.length);
   * ```
   */
  async ready(): Promise<void> {
    return this.#readyPromise;
  }

  /**
   * Force a full layout/render cycle.
   * Use this after programmatic changes that require re-measurement,
   * such as container resize or dynamic style changes.
   *
   * @group Lifecycle
   * @returns Promise that resolves when the render cycle completes
   *
   * @example
   * ```typescript
   * // After resizing the container
   * container.style.width = '800px';
   * await grid.forceLayout();
   * console.log('Grid re-rendered');
   * ```
   */
  async forceLayout(): Promise<void> {
    // If work is already pending, just flush at whatever phase was requested.
    // If nothing is pending, request FULL for backward compatibility
    // (callers may depend on forceLayout triggering a complete rebuild).
    if (this.#scheduler.pendingPhase === 0) {
      this.#scheduler.requestPhase(RenderPhase.FULL, 'forceLayout');
    }
    return this.#scheduler.whenReady();
  }

  /**
   * Get a frozen snapshot of the current effective configuration.
   * The returned object is read-only and reflects all merged config sources.
   *
   * @group Configuration
   * @returns Promise resolving to frozen configuration object
   *
   * @example
   * ```typescript
   * const config = await grid.getConfig();
   * console.log('Columns:', config.columns?.length);
   * console.log('Fit mode:', config.fitMode);
   * ```
   */
  async getConfig(): Promise<Readonly<GridConfig<T>>> {
    return Object.freeze({ ...(this.#effectiveConfig || {}) });
  }
  // #endregion

  // #region Row API (delegated to RowManager)
  /**
   * Get the unique ID for a row.
   * Uses the configured `getRowId` function or falls back to `row.id` / `row._id`.
   *
   * @group Data Management
   * @param row - The row object
   * @returns The row's unique identifier
   * @throws Error if no ID can be determined
   *
   * @example
   * ```typescript
   * const id = grid.getRowId(row);
   * console.log('Row ID:', id);
   * ```
   */
  getRowId(row: T): string {
    return resolveRowIdOrThrow(row, this.id, this.#effectiveConfig.getRowId);
  }

  /**
   * Get a row by its ID.
   * O(1) lookup via internal Map.
   *
   * @group Data Management
   * @param id - Row identifier (from getRowId)
   * @returns The row object, or undefined if not found
   *
   * @example
   * ```typescript
   * const row = grid.getRow('cargo-123');
   * if (row) {
   *   console.log('Found:', row.name);
   * }
   * ```
   */
  getRow(id: string): T | undefined {
    return this.#rowManager.getRow(id);
  }

  /**
   * Get a row and its current index by ID.
   * Used internally by plugins to resolve a row's current position in `_rows`
   * after sorting, filtering, or rows replacement.
   * @internal
   */
  _getRowEntry(id: string): { row: T; index: number } | undefined {
    this.#ensureRowIdMap();
    return this.#rowIdMap.get(id);
  }

  /**
   * Resolve a row by ID from the full source dataset, including rows that are
   * filtered or paged out of the visible `_rows`.
   *
   * Fast path: returns the visible entry (with its `_rows` index) when the row
   * is currently rendered. Slow path: linearly scans `sourceRows` and returns
   * `{ row, index: -1 }` when the row exists only in the source data (the `-1`
   * signals "not in the processed view"). Used by the row mutation APIs so a
   * filtered-out row can still be updated instead of crashing.
   * @internal
   */
  _getSourceRowEntry(id: string): { row: T; index: number } | undefined {
    const visible = this._getRowEntry(id);
    if (visible) return visible;

    const getRowId = this.#effectiveConfig.getRowId;
    const rows = this.#rows;
    for (let index = 0; index < rows.length; index++) {
      if (tryResolveRowId(rows[index], getRowId) === id) {
        return { row: rows[index], index: -1 };
      }
    }
    return undefined;
  }

  /**
   * Update a row by ID.
   * Mutates the row in-place and emits `cell-change` for each changed field.
   *
   * Resolves the row from the full dataset, so a row that is filtered or paged
   * out of the visible view can still be updated. If the ID matches no row at
   * all, a `TBW041` warning is logged and the call is a no-op (never throws);
   * with `source: 'history'` (an undo/redo replay of a since-deleted row) the
   * no-op is silent.
   *
   * **Filtered/paged-out rows:** the emitted `cell-change.rowIndex` is `-1`
   * (the row is not in the processed view). The update only schedules a
   * VIRTUALIZATION refresh — it does **not** re-run the row pipeline, so
   * changing a filtered/sorted/paged field will not move the row into or out
   * of view until the next ROWS-phase refresh (e.g. re-applying the filter or
   * reassigning `rows`).
   *
   * @group Data Management
   * @param id - Row identifier (from getRowId)
   * @param changes - Partial row data to merge
   * @param source - Origin of update (default: 'api')
   * @fires cell-change - For each field that changed
   *
   * @example
   * ```typescript
   * // WebSocket update handler
   * socket.on('cargo-update', (data) => {
   *   grid.updateRow(data.id, { status: data.status, eta: data.eta });
   * });
   * ```
   */
  updateRow(id: string, changes: Partial<T>, source: UpdateSource = 'api'): void {
    this.#rowManager.updateRow(id, changes, source);
  }

  /**
   * Batch update multiple rows.
   * More efficient than multiple `updateRow()` calls - single render cycle.
   *
   * Each row is resolved from the full dataset, so filtered/paged-out rows are
   * updated too. An unresolvable ID logs a `TBW041` warning (silent for
   * `source: 'history'`) and is skipped without aborting the rest of the batch
   * (never throws).
   *
   * **Filtered/paged-out rows:** for a row resolved from the source dataset but
   * not currently in the processed view, the emitted `cell-change.rowIndex` is
   * `-1`. These updates do **not** re-run the row pipeline (sort/filter/paging),
   * so a changed field won't move the row into or out of view until the next
   * ROWS-phase refresh.
   *
   * @group Data Management
   * @param updates - Array of { id, changes } objects
   * @param source - Origin of updates (default: 'api')
   * @fires cell-change - For each field that changed on each row
   *
   * @example
   * ```typescript
   * // Bulk status update
   * grid.updateRows([
   *   { id: 'cargo-1', changes: { status: 'shipped' } },
   *   { id: 'cargo-2', changes: { status: 'shipped' } },
   * ]);
   * ```
   */
  updateRows(updates: Array<{ id: string; changes: Partial<T> }>, source: UpdateSource = 'api'): void {
    this.#rowManager.updateRows(updates, source);
  }

  /**
   * Animate a row at the specified index.
   * Applies a visual animation to highlight changes, insertions, or removals.
   * Returns a `Promise` that resolves when the animation completes.
   *
   * **Animation types:**
   * - `'change'`: Flash highlight (for data changes, e.g., after cell edit)
   * - `'insert'`: Slide-in animation (for newly added rows)
   * - `'remove'`: Fade-out animation (for rows being removed)
   *
   * The animation is purely visual - it does not affect the data.
   * For remove animations, the row remains in the DOM until animation completes.
   *
   * @group Row Animation
   * @param rowIndex - Index of the row in the current row set
   * @param type - Type of animation to apply
   * @returns `true` if the row was found and animated, `false` otherwise
   *
   * @example
   * ```typescript
   * // Highlight a row and wait for the animation to finish
   * await grid.animateRow(rowIndex, 'change');
   *
   * // Fire-and-forget (animation runs in background)
   * grid.animateRow(rowIndex, 'insert');
   * ```
   */
  animateRow(rowIndex: number, type: RowAnimationType): Promise<boolean> {
    return animateRow(this, rowIndex, type);
  }

  /**
   * Animate multiple rows at once.
   * More efficient than calling `animateRow()` multiple times.
   * Returns a `Promise` that resolves when all animations complete.
   *
   * @group Row Animation
   * @param rowIndices - Indices of the rows to animate
   * @param type - Type of animation to apply to all rows
   * @returns Number of rows that were actually animated (visible in viewport)
   *
   * @example
   * ```typescript
   * // Highlight all changed rows after bulk update
   * const changedIndices = [0, 2, 5];
   * await grid.animateRows(changedIndices, 'change');
   * ```
   */
  animateRows(rowIndices: number[], type: RowAnimationType): Promise<number> {
    return animateRows(this, rowIndices, type);
  }

  /**
   * Animate a row by its ID.
   * Uses the configured `getRowId` function to find the row.
   * Returns a `Promise` that resolves when the animation completes.
   *
   * @group Row Animation
   * @param rowId - The row's unique identifier (from getRowId)
   * @param type - Type of animation to apply
   * @returns `true` if the row was found and animated, `false` otherwise
   *
   * @example
   * ```typescript
   * // Highlight a row after real-time update
   * socket.on('row-updated', async (data) => {
   *   grid.updateRow(data.id, data.changes);
   *   await grid.animateRowById(data.id, 'change');
   * });
   * ```
   */
  animateRowById(rowId: string, type: RowAnimationType): Promise<boolean> {
    return animateRowById(this, rowId, type);
  }

  /**
   * Insert a row at a specific position in the current view.
   *
   * The row is spliced into the visible (processed) row array at `index` and
   * appended to the source data so that future pipeline runs (sort, filter,
   * group) include it. No plugin processing is triggered — the row stays
   * exactly where you place it until the next full pipeline run.
   *
   * By default, an `'insert'` animation is applied. Pass `animate: false` to
   * skip the animation. The returned `Promise` resolves when the animation
   * completes (or immediately when `animate` is `false`).
   *
   * @group Data Management
   * @param index - Visible row index at which to insert (0-based)
   * @param row - The row data object to insert
   * @param animate - Whether to apply an 'insert' animation (default: `true`)
   *
   * @example
   * ```typescript
   * // Insert with animation (default)
   * grid.insertRow(3, { id: nextId(), name: '', status: 'Draft' });
   *
   * // Insert without animation
   * grid.insertRow(3, newRow, false);
   *
   * // Await animation completion
   * await grid.insertRow(3, newRow);
   * ```
   */
  async insertRow(index: number, row: T, animate = true): Promise<void> {
    return this.#rowManager.insertRow(index, row, animate);
  }

  /**
   * Remove a row at a specific position in the current view.
   *
   * The row is removed from both the visible (processed) row array and the
   * source data. No plugin processing is triggered — remaining rows keep their
   * current positions until the next full pipeline run.
   *
   * By default, a `'remove'` animation plays before the row is removed.
   * Pass `animate: false` to remove immediately. When animated, `await` the
   * result to ensure the row has been fully removed from data.
   *
   * @group Data Management
   * @param index - Visible row index to remove (0-based)
   * @param animate - Whether to apply a 'remove' animation first (default: `true`)
   * @returns The removed row object, or `undefined` if index was out of range
   *
   * @example
   * ```typescript
   * // Remove with fade-out animation (default)
   * await grid.removeRow(5);
   *
   * // Remove immediately, no animation
   * const removed = await grid.removeRow(5, false);
   * ```
   */
  async removeRow(index: number, animate = true): Promise<T | undefined> {
    return this.#rowManager.removeRow(index, animate);
  }

  /**
   * Apply a batch of row mutations (add, update, remove) in a single render cycle.
   *
   * This is the most efficient way to apply multiple row changes at once — ideal
   * for streaming data from WebSocket, SSE, or any real-time source.
   *
   * Operations are applied in order: removes → updates → adds. This ensures
   * updates don't target rows about to be removed, and adds don't collide
   * with existing rows.
   *
   * @group Data Management
   * @param transaction - The mutations to apply
   * @param animate - Whether to animate the changes (default: `true`)
   * @returns Result with the actual row objects affected
   *
   * @example
   * ```typescript
   * // Apply a mixed transaction
   * const result = await grid.applyTransaction({
   *   add: [{ id: 'new-1', name: 'Alice' }],
   *   update: [{ id: 'emp-5', changes: { status: 'Inactive' } }],
   *   remove: [{ id: 'emp-3' }],
   * });
   *
   * // Wire up a WebSocket stream
   * ws.onmessage = (e) => {
   *   const event = JSON.parse(e.data);
   *   grid.applyTransaction({
   *     [event.type]: event.type === 'update'
   *       ? [{ id: event.rowId, changes: event.changes }]
   *       : event.type === 'add'
   *         ? [event.row]
   *         : [{ id: event.rowId }],
   *   });
   * };
   * ```
   */
  async applyTransaction(transaction: RowTransaction<T>, animate = true): Promise<TransactionResult<T>> {
    return this.#rowManager.applyTransaction(transaction, animate);
  }

  /**
   * Apply a transaction asynchronously, batching rapid calls into a single render.
   *
   * Ideal for high-frequency streaming where many small updates arrive faster
   * than the browser can render. All transactions queued within the same
   * animation frame are merged and applied together.
   *
   * Animations are disabled for batched transactions to avoid visual overload.
   *
   * @group Data Management
   * @param transaction - The mutations to apply
   * @returns Result with the actual row objects affected (after batching)
   *
   * @example
   * ```typescript
   * // High-frequency WebSocket — updates batched into single renders
   * ws.onmessage = (e) => {
   *   const event = JSON.parse(e.data);
   *   grid.applyTransactionAsync({
   *     update: [{ id: event.id, changes: event.changes }],
   *   });
   * };
   * ```
   */
  applyTransactionAsync(transaction: RowTransaction<T>): Promise<TransactionResult<T>> {
    return this.#rowManager.applyTransactionAsync(transaction);
  }
  // #endregion

  // #region Focus & Navigation API (delegated to FocusManager)
  /**
   * Move focus to a specific cell.
   *
   * Accepts a column index (into the visible columns array) or a field name.
   * The grid scrolls the cell into view and applies focus styling.
   *
   * @group Focus & Navigation
   * @param rowIndex - The row index to focus (0-based, in the current processed row array)
   * @param column - Column index (0-based into visible columns) or field name string
   *
   * @example
   * ```typescript
   * // Focus by column index
   * grid.focusCell(0, 2);
   *
   * // Focus by field name
   * grid.focusCell(5, 'email');
   * ```
   */
  focusCell(rowIndex: number, column: number | string): void {
    this.#focusManager.focusCell(rowIndex, column);
  }

  /**
   * The currently focused cell position, or `null` if no rows are loaded.
   *
   * Returns a snapshot object with the row index, visible column index, and
   * the field name of the focused column.
   *
   * @group Focus & Navigation
   *
   * @example
   * ```typescript
   * const cell = grid.focusedCell;
   * if (cell) {
   *   console.log(`Focused on row ${cell.rowIndex}, column "${cell.field}"`);
   * }
   * ```
   */
  get focusedCell(): { rowIndex: number; colIndex: number; field: string } | null {
    return this.#focusManager.focusedCell;
  }

  /**
   * Scroll the viewport so a row is visible.
   *
   * Uses the grid's internal virtualization state (row height, position cache)
   * to calculate the correct scroll offset, including support for variable
   * row heights and grouped rows.
   *
   * @group Focus & Navigation
   * @param rowIndex - Row index (0-based, in the current processed row array)
   * @param options - Alignment and scroll behavior
   *
   * @example
   * ```typescript
   * // Scroll to row, only if not already visible
   * grid.scrollToRow(42);
   *
   * // Center the row in the viewport with smooth scrolling
   * grid.scrollToRow(42, { align: 'center', behavior: 'smooth' });
   * ```
   */
  scrollToRow(rowIndex: number, options?: ScrollToRowOptions): void {
    this.#focusManager.scrollToRow(rowIndex, options);
  }

  /**
   * Scroll the viewport so a row is visible, identified by its unique ID.
   *
   * Uses {@link GridConfig.getRowId | getRowId} (or the fallback `row.id` / `row._id`)
   * to find the row in the current (possibly sorted/filtered) data, then delegates
   * to {@link scrollToRow}.
   *
   * @group Focus & Navigation
   * @param rowId - The row's unique identifier
   * @param options - Alignment and scroll behavior
   *
   * @example
   * ```typescript
   * // After inserting a row, scroll to it by ID
   * grid.scrollToRowById('emp-42', { align: 'center' });
   * ```
   */
  scrollToRowById(rowId: string, options?: ScrollToRowOptions): void {
    this.#focusManager.scrollToRowById(rowId, options);
  }
  // #endregion

  // #region Column API
  /**
   * Show or hide a column by field name.
   *
   * @group Column Visibility
   * @param field - The field name of the column to modify
   * @param visible - Whether the column should be visible
   * @returns `true` if the visibility changed, `false` if unchanged
   * @fires column-state-change - Emitted when the visibility changes
   *
   * @example
   * ```typescript
   * // Hide the email column
   * grid.setColumnVisible('email', false);
   *
   * // Show it again
   * grid.setColumnVisible('email', true);
   * ```
   */
  setColumnVisible(field: string, visible: boolean): boolean {
    const result = this.#configManager.setColumnVisible(field, visible);
    if (result) {
      this.requestStateChange();
    }
    return result;
  }

  /**
   * Toggle a column's visibility.
   *
   * @group Column Visibility
   * @param field - The field name of the column to toggle
   * @returns The new visibility state (`true` = visible, `false` = hidden)
   * @fires column-state-change - Emitted when the visibility changes
   *
   * @example
   * ```typescript
   * // Toggle the notes column visibility
   * const isNowVisible = grid.toggleColumnVisibility('notes');
   * console.log(`Notes column is now ${isNowVisible ? 'visible' : 'hidden'}`);
   * ```
   */
  toggleColumnVisibility(field: string): boolean {
    const result = this.#configManager.toggleColumnVisibility(field);
    if (result) {
      this.requestStateChange();
    }
    return result;
  }

  /**
   * Check if a column is currently visible.
   *
   * @group Column Visibility
   * @param field - The field name to check
   * @returns `true` if the column is visible, `false` if hidden
   *
   * @example
   * ```typescript
   * if (grid.isColumnVisible('email')) {
   *   console.log('Email column is showing');
   * }
   * ```
   */
  isColumnVisible(field: string): boolean {
    return this.#configManager.isColumnVisible(field);
  }

  /**
   * Show all columns, resetting any hidden columns to visible.
   *
   * @group Column Visibility
   * @fires column-state-change - Emitted when column visibility changes
   * @example
   * ```typescript
   * // Reset button handler
   * resetButton.onclick = () => grid.showAllColumns();
   * ```
   */
  showAllColumns(): void {
    this.#configManager.showAllColumns();
    this.requestStateChange();
  }

  /**
   * Get metadata for all columns including visibility state.
   * Useful for building a column picker UI.
   *
   * @group Column Visibility
   * @returns Array of column info objects
   *
   * @example
   * ```typescript
   * // Build a column visibility menu
   * const columns = grid.getAllColumns();
   * columns.forEach(col => {
   *   if (!col.utility) { // Skip utility columns like selection checkbox
   *     const menuItem = document.createElement('label');
   *     menuItem.innerHTML = `
   *       <input type="checkbox" ${col.visible ? 'checked' : ''}>
   *       ${col.header}
   *     `;
   *     menuItem.querySelector('input').onchange = () =>
   *       grid.toggleColumnVisibility(col.field);
   *     menu.appendChild(menuItem);
   *   }
   * });
   * ```
   */
  getAllColumns(): Array<{
    field: string;
    header: string;
    visible: boolean;
    lockVisible?: boolean;
    utility?: boolean;
  }> {
    return this.#configManager.getAllColumns();
  }

  /**
   * Set the display order of columns.
   *
   * @group Column Order
   * @param order - Array of field names in desired order
   * @fires column-state-change - Emitted when column order changes
   *
   * @example
   * ```typescript
   * // Move 'status' column to first position
   * const currentOrder = grid.getColumnOrder();
   * const newOrder = ['status', ...currentOrder.filter(f => f !== 'status')];
   * grid.setColumnOrder(newOrder);
   * ```
   */
  setColumnOrder(order: string[]): void {
    this.#configManager.setColumnOrder(order);
    this.requestStateChange();
  }

  /**
   * Get the current column display order.
   *
   * @group Column Order
   * @returns Array of field names in display order
   *
   * @example
   * ```typescript
   * const order = grid.getColumnOrder();
   * console.log('Columns:', order.join(', '));
   * ```
   */
  getColumnOrder(): string[] {
    return this.#configManager.getColumnOrder();
  }

  /**
   * Get the current column state for persistence.
   * Returns a serializable object including column order, widths, visibility,
   * sort state, and any plugin-specific state.
   *
   * Use this to save user preferences to localStorage or a database.
   *
   * @group State Persistence
   * @returns Serializable column state object
   *
   * @example
   * ```typescript
   * // Save state to localStorage
   * const state = grid.getColumnState();
   * localStorage.setItem('gridState', JSON.stringify(state));
   *
   * // Later, restore the state
   * const saved = localStorage.getItem('gridState');
   * if (saved) grid.applyColumnState(JSON.parse(saved));
   * ```
   */
  getColumnState(): GridColumnState {
    const plugins = this.#pluginManager?.getAll() ?? [];
    return this.#configManager.collectState(plugins as BaseGridPlugin[]);
  }

  /**
   * Get the current column state.
   * Alias for `getColumnState()` for property-style read access.
   * @group State Persistence
   */
  get columnState(): GridColumnState | undefined {
    return this.getColumnState();
  }

  /**
   * Apply a previously saved column state, restoring column order, widths,
   * visibility, sort, and any plugin-contributed state. Can be called
   * before or after grid initialization — pre-init calls are deferred and
   * applied during setup.
   *
   * @group State Persistence
   * @fires column-state-change - Emitted after state is applied (if grid is initialized)
   * @example
   * ```typescript
   * // Restore saved state on page load
   * const grid = queryGrid('tbw-grid');
   * const saved = localStorage.getItem('myGridState');
   * if (saved) grid.applyColumnState(JSON.parse(saved));
   * ```
   */
  applyColumnState(state: GridColumnState | undefined): void {
    if (!state) return;

    // If already initialized, apply immediately and do NOT store as initial
    // state — the latter would be re-applied on the next #setup() (e.g. after
    // a subsequent `grid.columns = […]` reset), making the reset appear to be
    // a no-op until the user triggers it a second time.
    if (this.#initialized) {
      this.#applyColumnState(state);
      return;
    }

    // Store for use after initialization (called before ready)
    this.#initialColumnState = state;
    this.#configManager.initialColumnState = state;
  }

  /**
   * Apply column state internally.
   * Uses a fast path when only column widths changed (O(m) CSS update instead
   * of O(n) full row re-render). Falls back to full #setup() for structural
   * changes (visibility, order, sort).
   */
  #applyColumnState(state: GridColumnState): void {
    // Snapshot column order, visibility, and sort before applying state
    const prevColumns = this._columns;
    const prevSort = this._sortState;

    const plugins = (this.#pluginManager?.getAll() ?? []) as BaseGridPlugin[];
    this.#configManager.applyState(state, plugins);

    // If the applied state contains sort entries, plugins may have updated
    // their sort model (e.g., MultiSortPlugin clears _sortState after
    // restoring its own model). The core _sortState comparison in
    // isWidthOnlyChange cannot detect plugin-level sort changes, so always
    // take the structural path to ensure processRows re-sorts the data.
    // (Sort *removal* — going from sorted → no sort entries — is handled
    // by the sort-owning plugin via `requestRender()` after it clears its
    // model; core stays out of the plugin's sort lifecycle.)
    const hasPluginSort = state.columns.some((c) => c.sort !== undefined);

    // Detect if only widths changed (fast path: skip full row rebuild)
    if (!hasPluginSort && this.#isWidthOnlyChange(prevColumns, prevSort)) {
      // Invalidate visible columns cache (configManager bypasses grid._columns setter)
      this.#visibleColumnsCache = undefined;
      // Update CSS grid-template-columns — O(m) column count
      updateTemplate(this);
      // Notify plugins via afterRender hooks
      this.#scheduler.requestPhase(RenderPhase.STYLE, 'column-width');
      return;
    }

    // Structural change — full reconfiguration
    this.#setup();
  }

  /**
   * Check if the column state change only affected widths.
   * Compares current columns (post-applyState) against previous snapshot.
   * Returns false if column order, visibility, count, or sort changed.
   */
  #isWidthOnlyChange(prevColumns: ColumnInternal<T>[], prevSort: { field: string; direction: 1 | -1 } | null): boolean {
    const newColumns = this._columns;
    const newSort = this._sortState;

    // Sort changed
    if (prevSort?.field !== newSort?.field || prevSort?.direction !== newSort?.direction) return false;

    // Column count changed
    if (prevColumns.length !== newColumns.length) return false;

    for (let i = 0; i < prevColumns.length; i++) {
      const prev = prevColumns[i];
      const next = newColumns[i];
      // Order changed (different field at same index)
      if (prev.field !== next.field) return false;
      // Visibility changed (normalize undefined/false to boolean)
      if (!!prev.hidden !== !!next.hidden) return false;
    }

    return true;
  }

  // #endregion

  // #region Sort API
  /**
   * Get the current single-column sort state.
   *
   * Returns `null` when no sort is active.
   *
   * @group Sorting
   *
   * @example
   * ```typescript
   * const sort = grid.sortModel;
   * // { field: 'name', direction: 'desc' } | null
   * ```
   */
  get sortModel(): { field: string; direction: 'asc' | 'desc' } | null {
    if (!this._sortState) return null;
    return {
      field: this._sortState.field,
      direction: this._sortState.direction === 1 ? 'asc' : 'desc',
    };
  }

  /**
   * Sort by a column, toggle a column's sort direction, or clear sorting.
   *
   * - `sort('id', 'desc')` — apply sort with explicit direction
   * - `sort('id')` — toggle: none → asc → desc → none
   * - `sort(null)` — clear sort, restore original row order
   *
   * @param field - Column field to sort by, or `null` to clear
   * @param direction - Explicit direction; omit to toggle
   *
   * @group Sorting
   *
   * @example
   * ```typescript
   * grid.sort('id', 'desc');  // sort descending
   * grid.sort('price');       // toggle sort on price
   * grid.sort(null);          // clear sort
   * ```
   */
  sort(field: string | null, direction?: 'asc' | 'desc'): void {
    // Clear sort
    if (field === null) {
      if (!this._sortState) return;
      const prevField = this._sortState.field;
      this._sortState = null;
      this.__rowRenderEpoch++;
      for (let i = 0; i < this._rowPool.length; i++) this._rowPool[i].__epoch = -1;
      this._rows = this.__originalOrder.slice();
      this.__originalOrder = [];
      renderHeader(this);
      this.refreshVirtualWindow(true);
      this.dispatchEvent(new CustomEvent('sort-change', { detail: { field: prevField, direction: 0 } }));
      this._pluginManager?.emitPluginEvent?.('sort-change', { field: prevField, direction: 0 });
      this.requestStateChange?.();
      return;
    }

    const col = (this._columns as ColumnConfig<T>[]).find((c) => c.field === field);
    if (!col) return;

    if (direction) {
      // Explicit direction
      if (!this._sortState) this.__originalOrder = this._rows.slice();
      applySort(this, col, direction === 'desc' ? -1 : 1);
    } else {
      // Toggle: delegates to existing cycle logic
      toggleSort(this, col);
    }
  }
  // #endregion

  // #region Column State Persistence

  /**
   * Request a state change event to be emitted.
   * Called internally after resize, reorder, visibility, or sort changes.
   * Plugins should call this after changing their state.
   * The event is debounced to avoid excessive events during drag operations.
   * @group State Persistence
   * @internal Plugin API
   */
  requestStateChange(): void {
    const plugins = (this.#pluginManager?.getAll() ?? []) as BaseGridPlugin[];
    this.#configManager.requestStateChange(plugins);
  }

  /**
   * Reset column state to initial configuration.
   * Clears all user modifications including order, widths, visibility, and sort.
   *
   * @group State Persistence
   * @fires column-state-change - Emitted after state is reset
   * @example
   * ```typescript
   * // Reset button handler
   * resetBtn.onclick = () => {
   *   grid.resetColumnState();
   *   localStorage.removeItem('gridState');
   * };
   * ```
   */
  resetColumnState(): void {
    // Clear initial state
    this.#initialColumnState = undefined;
    this.__originalOrder = [];

    // Use ConfigManager to reset state
    const plugins = (this.#pluginManager?.getAll() ?? []) as BaseGridPlugin[];
    this.#configManager.resetState(plugins);

    // Re-initialize columns from config
    this.#configManager.merge();
    this.#setup();
  }
  // #endregion

  // #region Shell Refresh (internal plugin API)
  /**
   * The default row height in pixels.
   *
   * For fixed heights, this is the configured or CSS-measured row height.
   * For variable heights, this is the average/estimated row height.
   * Plugins should use this instead of hardcoding row heights.
   *
   * @group Virtualization
   */
  get defaultRowHeight(): number {
    return this._virtualization.rowHeight;
  }

  /** Pending shell refresh - used to batch multiple rapid calls */
  #pendingShellRefresh = false;

  /**
   * Re-parse light DOM shell elements and refresh shell header.
   * Call this after dynamically modifying <tbw-grid-header> children.
   *
   * Multiple calls are batched via microtask to avoid redundant DOM rebuilds
   * when registering multiple tool panels in sequence.
   *
   * @internal Plugin API
   */
  refreshShellHeader(): void {
    // Batch multiple rapid calls into a single microtask
    if (this.#pendingShellRefresh) return;
    this.#pendingShellRefresh = true;

    queueMicrotask(() => {
      this.#pendingShellRefresh = false;
      if (!this.isConnected) return;

      // Re-parse light DOM (header, tool buttons, and tool panels)
      this.#parseLightDom();

      // Mark sources as changed since shell parsing may have updated state maps
      this.#configManager.markSourcesChanged();

      // Re-merge config to sync shell state changes into effectiveConfig.shell
      this.#configManager.merge();

      // Prepare for re-render (plugins move relocated light-DOM nodes back).
      this.#pluginManager?.beforeStructuralRender();

      // Re-wrap the freshly prepared shell DOM via the ShellPlugin (fires the
      // afterStructuralRender hook). The plugin surgically rebuilds the shell
      // wrapper — preserving .tbw-grid-content and its listeners — re-establishes
      // its own listeners, and marks the controller initialized.
      this.#pluginManager?.afterStructuralRender();

      // Use lighter-weight post-render setup
      this.#afterShellRefresh();
    });
  }

  /**
   * Lighter-weight post-render setup after shell refresh.
   * Unlike #afterConnect(), this skips scheduler FULL request since DOM is already built.
   */
  #afterShellRefresh(): void {
    // Grid content was preserved by rebuildShellDOM, but the .tbw-grid-root
    // wrapper is new. Re-cache element references (same DOM nodes, new parent).
    const gridRoot = this.#cacheDomRefs();

    // Shell header content, custom toolbar buttons, default-open behavior, and
    // open-panel restoration are rendered by the ShellPlugin in its
    // `afterStructuralRender` hook (fired just above, before this method runs) —
    // core holds no shell-content knowledge (extraction #370, Task 1b,
    // Step 1b.1.3).

    // Re-create resize controller (DOM elements changed)
    this._resizeController?.dispose();
    this._resizeController = createResizeController(this);

    // Re-setup scroll listeners (DOM elements changed)
    this.#setupScrollListeners(gridRoot);

    // Request COLUMNS phase to reprocess columns (including column groups) and render header
    // #render() rebuilds the DOM with an empty header-row, so we need COLUMNS phase (5)
    // which includes processColumns (for column groups), processRows, and renderHeader
    this.#scheduler.requestPhase(RenderPhase.COLUMNS, 'shellRefresh');
  }
  // #endregion

  // #region Custom Styles API
  /** Map of registered custom stylesheets by ID - uses adoptedStyleSheets which survive DOM rebuilds */
  #customStyleSheets = new Map<string, CSSStyleSheet>();

  /**
   * Register custom CSS styles via `document.adoptedStyleSheets`.
   * Use this to style custom cell renderers, editors, or detail panels.
   *
   * Uses adoptedStyleSheets for efficiency - styles survive DOM rebuilds.
   *
   * @group Custom Styles
   * @param id - Unique identifier for the style block (for removal/updates)
   * @param css - CSS string to inject
   *
   * @example
   * ```typescript
   * // Register custom styles for a detail panel
   * grid.registerStyles('my-detail-styles', `
   *   .my-detail-panel { padding: 16px; }
   *   .my-detail-table { width: 100%; }
   * `);
   *
   * // Update styles later
   * grid.registerStyles('my-detail-styles', updatedCss);
   *
   * // Remove styles
   * grid.unregisterStyles('my-detail-styles');
   * ```
   */
  registerStyles(id: string, css: string): void {
    // Create or update the stylesheet
    let sheet = this.#customStyleSheets.get(id);
    if (!sheet) {
      sheet = new CSSStyleSheet();
      this.#customStyleSheets.set(id, sheet);
    }
    sheet.replaceSync(css);

    // Update adoptedStyleSheets to include all custom sheets
    this.#updateAdoptedStyleSheets();
  }

  /**
   * Remove previously registered custom styles.
   *
   * @group Custom Styles
   * @param id - The ID used when registering the styles
   */
  unregisterStyles(id: string): void {
    if (this.#customStyleSheets.delete(id)) {
      this.#updateAdoptedStyleSheets();
    }
  }

  /**
   * Get list of registered custom style IDs.
   *
   * @group Custom Styles
   */
  getRegisteredStyles(): string[] {
    return Array.from(this.#customStyleSheets.keys());
  }

  /**
   * Update document.adoptedStyleSheets to include custom sheets.
   * Without Shadow DOM, all custom styles go into the document.
   */
  #updateAdoptedStyleSheets(): void {
    const customSheets = Array.from(this.#customStyleSheets.values());

    // Start with document's existing sheets (excluding any we've added before)
    // We track custom sheets by their presence in our map
    const existingSheets = document.adoptedStyleSheets.filter(
      (sheet) => !Array.from(this.#customStyleSheets.values()).includes(sheet),
    );

    document.adoptedStyleSheets = [...existingSheets, ...customSheets];
  }
  // #endregion

  // #region External Focus Containers (delegated to FocusManager)
  /**
   * Register an external DOM element as a logical focus container of this grid.
   *
   * Focus moving into a registered container is treated as if it stayed inside
   * the grid: `data-has-focus` is preserved, click-outside commit is suppressed,
   * and the editing focus trap (when enabled) won't reclaim focus.
   *
   * Typical use case: overlay panels (datepickers, dropdowns, autocompletes)
   * that render at `<body>` level to escape grid overflow clipping.
   *
   * @group Focus Management
   * @param el - The external element to register
   *
   * @example
   * ```typescript
   * const overlay = document.createElement('div');
   * document.body.appendChild(overlay);
   *
   * // Tell the grid this overlay is "part of" the grid
   * grid.registerExternalFocusContainer(overlay);
   *
   * // Later, when overlay is removed
   * grid.unregisterExternalFocusContainer(overlay);
   * ```
   */
  registerExternalFocusContainer(el: Element): void {
    this.#focusManager.registerExternalFocusContainer(el);
  }

  /**
   * Unregister a previously registered external focus container.
   *
   * @group Focus Management
   * @param el - The element to unregister
   */
  unregisterExternalFocusContainer(el: Element): void {
    this.#focusManager.unregisterExternalFocusContainer(el);
  }

  /**
   * Check whether focus is logically inside this grid.
   *
   * Returns `true` when `document.activeElement` (or the given node) is
   * inside the grid's own DOM **or** inside any element registered via
   * {@link registerExternalFocusContainer}.
   *
   * @group Focus Management
   * @param node - Optional node to test. Defaults to `document.activeElement`.
   *
   * @example
   * ```typescript
   * if (grid.containsFocus()) {
   *   console.log('Grid or one of its overlays has focus');
   * }
   * ```
   */
  containsFocus(node?: Node | null): boolean {
    return this.#focusManager.containsFocus(node);
  }

  /**
   * The last meaningful element inside the grid host's light-DOM subtree
   * that received user focus. Excludes the grid host itself (synthetic
   * `tabindex=0` focus during keyboard navigation), bare `.cell` elements
   * (whose focus is virtual), and descendants of registered external focus
   * containers (overlays/datepickers/dropdowns). Returns `null` when the
   * user has not yet interacted with any tracked descendant.
   *
   * @group Focus Management
   */
  get lastFocusedElement(): HTMLElement | null {
    return this.#focusManager.lastFocusedElement;
  }

  /**
   * Restore focus to the last meaningful user-focused element inside the
   * grid (see {@link lastFocusedElement} for what counts as "meaningful").
   * The grid invokes this automatically whenever focus is bounced to
   * `<body>` (e.g. when a body-level overlay is removed). Exposed publicly
   * so application code can re-anchor focus on demand.
   *
   * @group Focus Management
   * @returns `true` if a tracked element existed and was refocused; `false` otherwise.
   */
  restoreLastFocus(): boolean {
    return this.#focusManager.restoreLastFocus();
  }
  // #endregion

  // #region Light DOM Helpers
  /**
   * Parse all light DOM shell elements in one call.
   * Consolidates parsing of header, tool buttons, and tool panels.
   */
  #parseLightDom(): void {
    // Generic seam — each plugin parses its own light-DOM contributions. The
    // ShellPlugin parses header / tool-buttons / tool-panels here (#370).
    this.#pluginManager?.parseLightDom();
  }

  /**
   * Set up Light DOM handlers via ConfigManager's observer infrastructure.
   * This handles frameworks like Angular that project content asynchronously.
   *
   * The observer is owned by ConfigManager (generic infrastructure).
   * The handlers (parsing logic) are owned here (eventually ShellPlugin).
   *
   * This separation allows plugins to register their own Light DOM elements
   * and handle parsing themselves.
   */
  #setupLightDomHandlers(): void {
    // Handler for column element changes
    const handleColumnChange = () => {
      this.#configManager.clearLightDomCache();
      this.#setup();
    };

    // Register handlers with ConfigManager.
    // Shell-related elements (tbw-grid-header / tbw-grid-tool-buttons /
    // tbw-grid-tool-panel) are registered by ShellPlugin.attach() via the
    // generic `_registerLightDomHandler` seam \u2014 core holds no shell tag
    // knowledge (extraction #370). Core owns only the column tags below + the
    // generic observer infrastructure.
    this.#configManager.registerLightDomHandler('tbw-grid-column', handleColumnChange);
    this.#configManager.registerLightDomHandler('tbw-grid-detail', handleColumnChange);
    this.#configManager.registerLightDomHandler('tbw-grid-type', handleColumnChange);

    // Start observing
    this.#configManager.observeLightDOM(this);
  }

  /**
   * Re-parse light DOM column elements and refresh the grid.
   * Call this after framework adapters have registered their templates.
   * Uses the render scheduler to batch with other pending updates.
   * @category Framework Adapters
   */
  refreshColumns(): void {
    // Clear the column cache to force re-parsing
    this.#configManager.clearLightDomCache();

    // Invalidate cell cache to reset __hasSpecialColumns flag
    // This is critical for frameworks like React where renderers are registered asynchronously
    // after the initial render (which may have cached __hasSpecialColumns = false)
    invalidateCellCache(this);

    // Re-parse light DOM columns SYNCHRONOUSLY to pick up newly registered framework renderers
    // This must happen before the scheduler runs processColumns
    this.#configManager.parseLightDomColumns(this);

    // Re-sync plugin light-DOM state (frameworks may have projected shell
    // elements — title / tool buttons — asynchronously after the initial
    // render). Each plugin re-parses and refreshes its own chrome in place via
    // the generic `syncLightDom` hook; core holds no shell knowledge (#370).
    this.#pluginManager?.syncLightDom();

    // Request a COLUMNS phase render through the scheduler
    // This batches with any other pending work (e.g., afterConnect)
    this.#scheduler.requestPhase(RenderPhase.COLUMNS, 'refreshColumns');
  }
  // #endregion

  // #region Virtualization (delegated to VirtualizationManager)

  /**
   * Update cached viewport and faux scrollbar geometry.
   * Called by ResizeObserver and on force-refresh to avoid forced layout reads during scroll.
   * @internal
   */
  #updateCachedGeometry(): void {
    this.#virtManager.updateCachedGeometry();
  }

  /**
   * Core virtualization routine. Delegates to VirtualizationManager.
   * @param force - Whether to force a full refresh (not just scroll update)
   * @param skipAfterRender - When true, skip calling afterRender (used by scheduler which calls it separately)
   * @returns Whether the visible row window changed (start/end differ from previous)
   * @internal Plugin API
   */
  refreshVirtualWindow(force = false, skipAfterRender = false): boolean {
    return this.#virtManager.refreshVirtualWindow(force, skipAfterRender);
  }

  /**
   * Invalidate a row's height in the position cache.
   * Call this when a plugin changes a row's height (e.g., expanding/collapsing a detail panel).
   * @param rowIndex - Index of the row whose height changed
   * @param newHeight - Optional new height. If not provided, queries plugins for height.
   */
  invalidateRowHeight(rowIndex: number, newHeight?: number): void {
    this.#virtManager.invalidateRowHeight(rowIndex, newHeight);
  }

  // #endregion

  // #region Render
  #render(): void {
    // Parse light DOM shell elements before rendering
    this.#parseLightDom();

    // Mark sources as changed since shell parsing may have updated state maps
    this.#configManager.markSourcesChanged();

    // Re-merge config to pick up any newly parsed light DOM shell settings
    this.#configManager.merge();

    // Build the BARE grid DOM (no shell chrome). The shell, when configured, is
    // wrapped around this freshly built grid by the auto-registered ShellPlugin
    // in the afterStructuralRender hook below — core no longer constructs shell
    // DOM itself (extraction #370, Task 1b: DOM ownership inversion).
    buildBareGridDOMIntoElement(this.#renderRoot);

    // Notify plugins that the root DOM structure was (re)built, synchronously
    // and before paint. The ShellPlugin wraps the bare grid in shell chrome here
    // (via rebuildShellDOM), sets up its listeners, and marks the controller
    // initialized — core holds no shell-construction knowledge in this path.
    this.#pluginManager?.afterStructuralRender();
  }
  // #endregion
}

// #region Self-registration (multi-version safe)
/**
 * Register `DataGridElement` with the platform `customElements` registry.
 *
 * The custom-elements registry is realm-global and first-wins: once a tag
 * name is defined, subsequent `define()` calls for the same name throw. When
 * two micro-frontend widgets on the same page bundle different versions of
 * `@toolbox-web/grid`, the second bundle to load would either crash or have
 * its grid silently rendered by the first bundle's (possibly incompatible)
 * class.
 *
 * Algorithm:
 *  1. If `tbw-grid` is not yet registered, register THIS class as `tbw-grid`
 *     (the common single-version case — `activeTag` stays `'tbw-grid'`).
 *  2. If `tbw-grid` is already registered AND the existing class reports the
 *     same `version`, reuse it — same code, no need to re-register. The
 *     existing class wins, but `activeTag` still points at `tbw-grid` so
 *     adapters render the bare tag.
 *  3. Otherwise, register THIS class under a version-suffixed tag
 *     (`tbw-grid-v<version-with-dashes>`) and point `activeTag` at it. Two
 *     copies of the same version always share; only different versions get
 *     suffixed.
 *
 * Issue: #339 — "grid: support multiple grid versions on one page via
 * auto-suffixed tag names".
 */
function registerDataGrid(): void {
  const base = DataGridElement.tagName;
  const existing = customElements.get(base) as typeof DataGridElement | undefined;
  if (!existing) {
    customElements.define(base, DataGridElement);
    DataGridElement.activeTag = base;
    return;
  }
  if (existing.version === DataGridElement.version) {
    DataGridElement.activeTag = base;
    return;
  }
  // Sanitize the version into a valid custom-element name suffix: only
  // ASCII letters, digits, and dashes. Replace any other characters
  // (notably `.` from semver and `+`/`-` from pre-release tags) with `-`.
  const versionSuffix = DataGridElement.version.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
  const suffixed = `${base}-v${versionSuffix}`;
  if (!customElements.get(suffixed)) {
    customElements.define(suffixed, DataGridElement);
  }
  DataGridElement.activeTag = suffixed;
}
registerDataGrid();

// Make DataGridElement accessible globally for framework adapters.
// Like the custom-element registry, this alias is realm-global and first-wins:
// the *first* loaded grid bundle's class becomes `globalThis.DataGridElement`.
// Subsequent bundles keep their class accessible via the named export and via
// `customElements.get(activeTag)`, so adapters MUST NOT rely on the global.
if (!(globalThis as { DataGridElement?: typeof DataGridElement }).DataGridElement) {
  globalThis.DataGridElement = DataGridElement;
}
// #endregion

// Type augmentation for querySelector/createElement and globalThis
declare global {
  var DataGridElement: typeof import('./grid').DataGridElement;
  interface HTMLElementTagNameMap {
    'tbw-grid': DataGridElement;
  }
}
